import { Fragment, createContext, useCallback, useContext, useEffect, useMemo, useRef, useState, type ReactNode } from 'react';
import { createPortal } from 'react-dom';
import { Link, NavLink, useLocation, useNavigate } from 'react-router-dom';
import { doc, getDoc } from 'firebase/firestore';
import {
  AppShell,
  AppMain,
  AppContent,
  Sidebar,
  SidebarHeader,
  SidebarNav,
  SidebarNavItem,
  SidebarFooter,
  AppHeader,
  AppHeaderLeft,
  AppHeaderRight,
  Avatar,
  AvatarFallback,
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
  Separator,
} from '@cyber-aware/ui';
import {
  Archive,
  ClipboardList,
  FileJson,
  LayoutDashboard,
  Server,
  ShieldAlert,
  GraduationCap,
  KeyRound,
  Library,
  Menu,
  Search,
  MessageSquare,
  PanelLeftClose,
  PanelLeftOpen,
  Settings,
  LogOut,
  ChevronDown,
  ChevronRight,
  UserCircle,
  Activity,
  AtSign,
  BarChart3,
  BookOpen,
  Bot,
  Building2,
  FileText,
  Flag,
  Gauge,
  Globe,
  Home,
  Laptop,
  LayoutGrid,
  LayoutTemplate,
  Lock,
  LogIn,
  Mail as MailIcon,
  Network,
  Plus,
  RefreshCw,
  Send,
  Shield,
  ShieldCheck,
  Sparkles,
  Users,
  X,
  Zap,
  AlertTriangle,
  Cloud,
  FileCode,
  FolderOpen,
  Package,
  Plug,
  ShieldOff,
  ScanEye,
  Radar,
} from 'lucide-react';
import { useAuth } from '../../auth/AuthContext';
import { ThemeToggle } from '../ThemeToggle';
import { useMemberships } from '../../auth/useMemberships';
import { useEntitlement, useOrgEntitlements } from '../../lib/entitlements';
// MP316c — Feature-flag SDK. Filters both the per-app sidebar
// (via item.featureFlag) and gates individual chrome affordances
// (Ask Athena) without a route reload.
import { useAllFeatureFlags } from '../../lib/useFeatureFlag';
import { PreviewBadge } from './PreviewBadge';
import { listAgentApprovals } from '../../lib/agentApprovals';
import { db } from '../../services/firebaseConfig';
import { BackToAppsButton } from '../layout/BackToAppsButton';
import { AthenaPanel } from '../athena/AthenaPanel';
import { useOptionalAthena } from '../athena/AthenaProvider';
import { useDashboardMenu } from '../../lib/useDashboardMenu';
import { usePolicyMenu } from '../../lib/usePolicyMenu';
import { configurationMenuItems } from '../../lib/configurationMenu';
import { GO_TO_WINDOW_MS, goToStep, goToTargetsFor, type GoToState } from '../../lib/goToShortcuts';
// MP738 — standalone-app mode: on a standalone product's own host the
// shell renders that one product (lib/standaloneApp.ts).
import { currentStandaloneBrand } from '../../lib/tenantHost';
import { STANDALONE_HIDDEN_TENANT_SECTIONS, appsForHost } from '../../lib/standaloneApp';
import { GoToHint, ShortcutsDialog } from './KeyboardShortcuts';
import { HelpMenu } from './HelpMenu';
import { NotificationsBell } from './NotificationsBell';
import {
  APPS,
  TENANT_SIDEBAR,
  getApp,
  resolveAppFromPathname,
  type AppDefinition,
  type AppKey,
  type AppSidebarItem,
  type AppPrimaryAction,
  type AppSidebarSection,
  type IconRegistry,
  type TenantSidebarConfig,
} from '../../lib/apps';
import { ArrowRight, ExternalLink, Grid3x3 } from 'lucide-react';
import { CommandPalette } from './CommandPalette';
import {
  buildPaletteEntries,
  documentTitleFor,
  isTypingTarget,
  pushRecent,
  type PaletteSource,
  type PaletteSourceItem,
} from '../../lib/commandPalette';
import { useFocusAppKeys } from '../../lib/useFocusAppKeys';

// Icon registry — maps the iconKey strings in apps.ts to the actual
// lucide-react components. Centralised here so apps.ts stays a pure
// data file with no JSX or component imports.
const ICONS: IconRegistry = {
  dashboard: LayoutDashboard,
  home: Home,
  'building-2': Building2,
  grid: Grid3x3,
  'shield-alert': ShieldAlert,
  // MP124 — Platform consolidation icons. Platform parent gets
  // shield-check (distinct from Policies' plain shield); Admins
  // child gets user-circle.
  'shield-check': ShieldCheck,
  'user-circle': UserCircle,
  'graduation-cap': GraduationCap,
  'message-square': MessageSquare,
  mail: MailIcon,
  laptop: Laptop,
  globe: Globe,
  bot: Bot,
  gauge: Gauge,
  activity: Activity,
  users: Users,
  'log-in': LogIn,
  'refresh-cw': RefreshCw,
  lock: Lock,
  'at-sign': AtSign,
  zap: Zap,
  send: Send,
  'key-round': KeyRound,
  shield: Shield,
  settings: Settings,
  sparkles: Sparkles,
  'book-open': BookOpen,
  'file-text': FileText,
  'bar-chart-3': BarChart3,
  // MP57 — Agents catalog sidebar item
  library: Library,
  // MP66 — KPI + Phishing campaign sub-menu icons
  plus: Plus,
  'layout-grid': LayoutGrid,
  'layout-template': LayoutTemplate,
  // MP89 — Phishing "Reports" aside item
  flag: Flag,
  // MP254 — Agents "Orchestration" sidebar item
  network: Network,
  // MP700 — Resilience sidebar. `lock` (Vault), `shield-check`
  // (Drills), `dashboard` (Overview) and `settings` are reused from
  // above; these three are new.
  server: Server,
  archive: Archive,
  'clipboard-list': ClipboardList,
  // MP438 — Surface "SBOMs" sidebar item
  'file-json': FileJson,
  // Menu review (Oct 2026) — Posture's navigation moved from its
  // in-page rail into the main sidebar; these are the rail's icons.
  'alert-triangle': AlertTriangle,
  cloud: Cloud,
  'file-code': FileCode,
  'folder-open': FolderOpen,
  package: Package,
  plug: Plug,
  'shield-off': ShieldOff,
  // X-One AI Security's app icon.
  'scan-eye': ScanEye,
  search: Search,
  // MP900 — X-One Exposure's app icon.
  radar: Radar,
};

function renderIcon(key: string, className = 'h-4 w-4'): ReactNode {
  const Cmp = ICONS[key];
  if (!Cmp) return null;
  return <Cmp className={className} />;
}

interface NavItem {
  to: string;
  label: string;
  icon: ReactNode;
  end?: boolean;
  /** Heading shown above the row when it starts a new group. */
  group?: string;
  /** Link to a page whose own row lives elsewhere (see AppSidebarItem). */
  shortcut?: boolean;
  /** Short muted figure at the row's end. */
  hint?: string;
  /** MP58 — Optional nested sub-items. When present the row renders
   *  with a chevron + a collapsible group of indented children.
   *  Auto-expands when any child route is active or the parent
   *  route is. */
  children?: NavItem[];
  /** MP306 — Optional unread count. When > 0 the row renders a small
   *  primary-coloured pill after the label so the admin sees pending
   *  work at a glance. Used today for the Agent Approvals row so a
   *  new pending approval surfaces without waiting for email. Empty
   *  string when 0 / missing so nothing renders. */
  badge?: number;
  /** MP316i — true when the row's `featureFlag` resolves `isBeta` for
   *  this org, i.e. the viewer sees the surface because they are on the
   *  beta channel. Renders the Preview chip after the label. */
  preview?: boolean;
}

interface NavSection {
  title: string | null;
  items: NavItem[];
}

// MP122 — Apps row is rendered specially: it's a toggle button
// (not a NavLink) that flips `appsAsideOpen` without navigating.
// The discriminator lets the render loop pick out where to drop
// the toggle in the section order — same placement as the old
// MP54 accordion. Hover-preview still works because the marker
// carries the entitled-apps list for the overlay to show.
interface AppsToggleMarker {
  kind: 'apps-toggle';
}
type SidebarEntry = NavSection | AppsToggleMarker;
function isAppsToggleMarker(entry: SidebarEntry): entry is AppsToggleMarker {
  return (entry as AppsToggleMarker).kind === 'apps-toggle';
}

// Convert an AppSidebarSection (data) to a rendered NavSection (icons
// resolved via the registry). Used both for the per-app section at
// the top of the sidebar and the tenant-wide chrome below.
function toNavItem(
  it: AppSidebarItem,
  badges: Record<string, number> = {},
  previewKeys: ReadonlySet<string> = new Set(),
): NavItem {
  return {
    to: it.to,
    label: it.label,
    icon: renderIcon(it.iconKey),
    end: it.end,
    ...(it.group ? { group: it.group } : {}),
    ...(it.shortcut ? { shortcut: true } : {}),
    ...(it.hint ? { hint: it.hint } : {}),
    ...(it.children ? { children: it.children.map((c) => toNavItem(c, badges, previewKeys)) } :{}),
    // MP306 — Match by route path so callers don't need to know
    // symbolic keys; the sidebar data (apps.ts) stays badge-free.
    ...(badges[it.to] ? { badge: badges[it.to] } : {}),
    // MP316i — Preview chip when the row's flag resolves isBeta.
    ...(it.featureFlag && previewKeys.has(it.featureFlag) ? { preview: true } : {}),
  };
}

// MP58 — Recursive nav row with sub-menu support.
// MP60 — Fixed the collapse bug: expansion state is now
// (a) URL-derived by default (auto-open when on the parent's
// route or any child's), AND (b) user-overridable via the
// chevron button (toggle persists in localStorage). Clicking
// the parent's label NAVIGATES (NavLink); clicking the chevron
// TOGGLES expansion without navigating. The two click targets
// are split so navigation + collapse are independent gestures.
/** Arrow-key movement inside a grid of menu items (the apps switcher).
 *  Radix menus move up/down through items in DOM order only; in a grid
 *  left/right should step one tile and up/down one row. Off the grid's
 *  top or bottom edge, focus goes to the menu item before/after the grid
 *  ("All apps"). Handlers call preventDefault so Radix's own arrow
 *  handling stands down for the keys handled here. */
function gridMenuKeyDown(e: React.KeyboardEvent<HTMLElement>): void {
  const cols = typeof window !== 'undefined' && window.matchMedia?.('(min-width: 640px)').matches ? 3 : 1;
  const steps: Record<string, number> = { ArrowRight: 1, ArrowLeft: -1, ArrowDown: cols, ArrowUp: -cols };
  const step = steps[e.key];
  if (step === undefined) return;
  const grid = e.currentTarget.closest('[data-menu-grid]');
  if (!grid) return;
  const items = Array.from(grid.querySelectorAll<HTMLElement>('[data-menu-grid-item]'));
  const i = items.indexOf(e.currentTarget);
  const next = items[i + step];
  e.preventDefault();
  if (next) {
    next.focus();
    return;
  }
  if (Math.abs(step) === cols) {
    const all = Array.from(grid.closest('[role="menu"]')?.querySelectorAll<HTMLElement>('[role^="menuitem"]') ?? []);
    const edge = step > 0 ? items[items.length - 1] : items[0];
    all[all.indexOf(edge) + (step > 0 ? 1 : -1)]?.focus();
  }
}

/** A small heading before the first row of each group of sub-items. */
function GroupHeading({ items, index }: { items: readonly { group?: string }[]; index: number }) {
  const g = items[index].group;
  if (!g || (index > 0 && items[index - 1].group === g)) return null;
  return (
    <div className={`px-2 pb-0.5 text-[10px] font-semibold uppercase tracking-wider text-muted-foreground/80 ${index > 0 ? 'pt-2' : 'pt-0.5'}`}>
      {g}
    </div>
  );
}

// A fly-out opened from inside the hover panel keeps that panel open
// while the pointer is over it (the panel's own mouseleave would
// otherwise close both).
const FlyoutHoldContext = createContext<{ hold: () => void; release: () => void } | null>(null);

function NavRow({
  item,
  collapsed,
  depth,
  currentPath,
  currentHash,
  manualOverride,
  onToggleExpand,
  showAsLeaf = false,
  suppressActive = false,
  hoverFlyout = false,
}: {
  item: NavItem;
  collapsed: boolean;
  /** 0 = top-level. 1+ = indented child. Used to set left padding. */
  depth: number;
  currentPath: string;
  /** location.hash from useLocation. Used for hash-fragment active
   *  state on child items that point at /page#section URLs. */
  currentHash: string;
  /** MP60 — User's manual toggle state, keyed by parent's `to`.
   *  Map<parentTo, isOpen>. When a key exists for this row's parent,
   *  it overrides the URL-derived auto-expansion. When absent,
   *  auto-expansion based on URL match wins. */
  manualOverride: Map<string, boolean>;
  /** MP60 — Called when the user clicks the chevron. Parent owns
   *  the manualOverride Map; this callback flips the entry for
   *  this item's `to`. */
  onToggleExpand: (parentTo: string, nextOpen: boolean) => void;
  /** MP67 — When true, render this row as a single nav link without
   *  the chevron + inline accordion. Used by the main sidebar when
   *  an item's children are surfaced in the per-tenant-section
   *  secondary sidebar instead — keeps the chrome from doubling. */
  showAsLeaf?: boolean;
  /** Drop the visual "you are here" highlight while another panel (the
   *  Apps panel) has the user's attention, so only one row is lit.
   *  NavLink still sets aria-current, so the current page is still
   *  announced to screen readers. */
  suppressActive?: boolean;
  /** Rows with children open them in a fly-out beside the panel on
   *  hover (operator request, 23 Oct: Dashboards under Insights). The
   *  chevron still expands them in place for keyboard and touch. */
  hoverFlyout?: boolean;
}): ReactNode {
  // Strip hash for NavLink (React Router doesn't match on hash).
  const toPath = item.to.split('#')[0];
  const hashTarget = item.to.includes('#') ? `#${item.to.split('#')[1]}` : '';

  // Child active = either the URL hash matches (for /page#section
  // children) OR the URL path matches the child's path.
  const isChildActive = (child: NavItem): boolean => {
    if (child.shortcut) return false;
    const childPath = child.to.split('#')[0];
    const childHash = child.to.includes('#') ? `#${child.to.split('#')[1]}` : '';
    if (childHash) return currentPath === childPath && currentHash === childHash;
    if (child.end) return currentPath === childPath;
    return currentPath === childPath || currentPath.startsWith(childPath + '/');
  };

  // MP67 — `showAsLeaf` collapses the row to a single nav link;
  // chevron + inline accordion both keyed off `hasChildren` so
  // suppressing here is sufficient.
  const hasChildren = !showAsLeaf && !!item.children && item.children.length > 0;
  const anyChildActive = hasChildren && item.children!.some(isChildActive);
  const parentPathActive = item.end ? currentPath === toPath : currentPath === toPath || currentPath.startsWith(toPath + '/');
  // MP60 — Resolution order: user manual override → URL-derived
  // auto-expansion. A user who clicks the chevron to collapse a
  // parent on its own route gets a closed sub-menu (was impossible
  // before because expanded was purely derived).
  const autoExpanded = hasChildren && (anyChildActive || parentPathActive);
  const override = manualOverride.get(item.to);
  const expanded = hasChildren && (override !== undefined ? override : autoExpanded);

  // Hover fly-out. Panels scroll (overflow), which would clip an
  // absolutely placed child, so the fly-out is portalled and fixed to
  // the row's position. Timers absorb the gap between row and panel.
  const canFly = hoverFlyout && hasChildren && !expanded && !collapsed;
  const [flyAt, setFlyAt] = useState<{ left: number; top: number } | null>(null);
  const flyTimer = useRef<number | null>(null);
  const clearFlyTimer = () => {
    if (flyTimer.current !== null) window.clearTimeout(flyTimer.current);
    flyTimer.current = null;
  };
  const openFly = (el: HTMLElement) => {
    clearFlyTimer();
    flyTimer.current = window.setTimeout(() => {
      const r = el.getBoundingClientRect();
      setFlyAt({ left: r.right + 6, top: Math.max(8, Math.min(r.top - 8, window.innerHeight - 200)) });
    }, 80);
  };
  const closeFlySoon = () => {
    clearFlyTimer();
    flyTimer.current = window.setTimeout(() => setFlyAt(null), 200);
  };
  const parentHold = useContext(FlyoutHoldContext);
  useEffect(() => () => clearFlyTimer(), []);
  useEffect(() => {
    if (!canFly) setFlyAt(null);
  }, [canFly]);

  return (
    <>
      {/* MP60 — Split the row: NavLink for the label/icon (navigates)
          + separate button for the chevron (toggles only). Wrapped in
          a relative div so the active-bar pseudo-element on NavLink
          still positions correctly. */}
      <div
        className={`relative flex items-stretch ${depth > 0 ? 'pl-5' : ''}`}
        onMouseEnter={canFly ? (e) => openFly(e.currentTarget) : undefined}
        onMouseLeave={canFly ? closeFlySoon : undefined}
        onKeyDown={flyAt ? (e) => e.key === 'Escape' && setFlyAt(null) : undefined}
      >
        <NavLink
          to={`${toPath}${hashTarget}`}
          end={item.end}
          className="block flex-1 min-w-0"
          title={collapsed ? item.label : undefined}
        >
          {({ isActive }) => {
            // For parents with children, the active indicator goes on
            // the matching CHILD; the parent gets a neutral background
            // when expanded.
            // NavLink matches the path only, so rows that differ by #section
            // (the Policies categories) would all light up together; a
            // hash row is active only when its hash is the current one.
            const showActive =
              isActive &&
              !anyChildActive &&
              !suppressActive &&
              !item.shortcut &&
              (!hashTarget || currentHash === hashTarget);
            return (
              // MP123 — Collapsed mode bypasses SidebarNavItem because
              // the primitive wraps children in its own truncate span
              // that fights flex-col layout and prevents the label
              // from centering / truncating properly. Plain div with
              // full layout control here; SidebarNavItem only in
              // expanded mode.
              collapsed ? (
                <div
                  className={`group flex flex-col items-center gap-1 rounded-md px-1 py-2 text-sm font-medium text-muted-foreground transition-colors hover:bg-accent hover:text-foreground [&>span:last-child]:line-clamp-2 [&>span:last-child]:whitespace-normal [&>span:last-child]:text-[11px] [&>span:last-child]:hyphens-auto [&>span:last-child]:[overflow-wrap:anywhere] ${
                    showActive
                      ? 'relative bg-accent text-foreground before:absolute before:left-0 before:top-2 before:bottom-2 before:w-0.5 before:rounded-r before:bg-primary'
                      : ''
                  }`}
                >
                  <span
                    className={`relative flex shrink-0 items-center justify-center transition-colors h-6 w-6 [&>svg]:h-6 [&>svg]:w-6 ${showActive ? 'text-primary' : ''}`}
                  >
                    {item.icon}
                    {/* MP306 — Tiny dot on the icon when collapsed
                        (no room for the count itself). Still red so
                        the operator can see something wants their
                        attention from the collapsed rail. */}
                    {item.badge && item.badge > 0 && (
                      <span
                        className="absolute -right-1 -top-1 h-2 w-2 rounded-full bg-rose-500 ring-1 ring-background"
                        aria-hidden="true"
                      />
                    )}
                    {item.badge && item.badge > 0 && (
                      <span className="sr-only">{`${item.badge} pending`}</span>
                    )}
                  </span>
                  <span className="block w-full truncate text-center text-[11px] font-medium leading-tight">
                    {item.label}
                  </span>
                </div>
              ) : (
                <SidebarNavItem
                  icon={item.icon}
                  iconSize={depth > 0 ? 'sm' : 'md'}
                  active={showActive}
                  // The row is a <button>, which sizes to its text unless
                  // told otherwise; w-full + min-w-0 keep it to the panel's
                  // width so long names end in "…" instead of running off.
                  className={`w-full min-w-0 ${
                    showActive
                      ? 'relative before:absolute before:left-0 before:top-2 before:bottom-2 before:w-0.5 before:rounded-r before:bg-primary'
                      : ''
                  } ${depth > 0 ? 'pl-3 text-[13px]' : ''}`}
                >
                  <span className="flex min-w-0 flex-1 items-center gap-2 truncate" title={item.label}>
                    <span className="truncate">{item.label}</span>
                    {/* MP316i — Preview chip: this org sees the row via a
                        flag's beta channel. */}
                    {item.preview && <PreviewBadge />}
                    {item.hint && (
                      <span className="ml-auto shrink-0 text-[11px] tabular-nums text-muted-foreground">{item.hint}</span>
                    )}
                    {/* MP306 — Numeric badge when expanded. Rose fill
                        so it stands out from every existing accent
                        colour in the shell. Caps at 99+ to keep the
                        pill narrow. */}
                    {item.badge && item.badge > 0 && (
                      <span
                        className="ml-auto inline-flex min-w-[1.25rem] items-center justify-center rounded-full bg-rose-500 px-1.5 text-[10px] font-semibold text-white"
                        aria-label={`${item.badge} pending`}
                      >
                        {item.badge > 99 ? '99+' : item.badge}
                      </span>
                    )}
                  </span>
                </SidebarNavItem>
              )
            );
          }}
        </NavLink>
        {hasChildren && !collapsed && (
          <button
            type="button"
            onClick={(e) => {
              e.preventDefault();
              e.stopPropagation();
              onToggleExpand(item.to, !expanded);
            }}
            aria-label={expanded ? `Collapse ${item.label}` : `Expand ${item.label}`}
            aria-expanded={expanded}
            className="inline-flex h-7 w-7 shrink-0 items-center justify-center self-center rounded text-muted-foreground hover:bg-accent hover:text-foreground"
          >
            {expanded ? <ChevronDown className="h-3 w-3" /> : <ChevronRight className="h-3 w-3" />}
          </button>
        )}
      </div>
      {hasChildren && expanded && !collapsed && (
        <div className="flex flex-col gap-0.5">
          {item.children!.map((child, i) => (
            <Fragment key={`${child.to}|${child.label}`}>
            <GroupHeading items={item.children!} index={i} />
            <NavRow
              item={child}
              collapsed={collapsed}
              depth={depth + 1}
              currentPath={currentPath}
              currentHash={currentHash}
              manualOverride={manualOverride}
              onToggleExpand={onToggleExpand}
            />
            </Fragment>
          ))}
        </div>
      )}
      {canFly &&
        flyAt &&
        createPortal(
          <nav
            aria-label={item.label}
            className={`fixed z-[60] hidden max-h-[calc(100vh-16px)] w-64 flex-col overflow-y-auto p-1.5 md:flex ${FLOATING_PANEL}`}
            style={{ left: flyAt.left, top: flyAt.top }}
            onMouseEnter={() => {
              clearFlyTimer();
              parentHold?.hold();
            }}
            onMouseLeave={() => {
              closeFlySoon();
              parentHold?.release();
            }}
          >
            <div className="px-2 pb-1 pt-1.5 text-[11px] font-semibold uppercase tracking-wider text-muted-foreground">
              {item.label}
            </div>
            {item.children!.map((child, i) => (
              <Fragment key={`${child.to}|${child.label}`}>
                <GroupHeading items={item.children!} index={i} />
                <NavRow
                  item={child}
                  collapsed={false}
                  depth={0}
                  currentPath={currentPath}
                  currentHash={currentHash}
                  manualOverride={manualOverride}
                  onToggleExpand={onToggleExpand}
                />
              </Fragment>
            ))}
          </nav>,
          document.body,
        )}
    </>
  );
}

/** An app's main "create" action at the top of its menu (menu review,
 *  Oct 2026): a button, not a nav row, so it reads as an action. Soft
 *  style — light tint, primary text, thin border, solid only on hover —
 *  so an always-visible sidebar control doesn't outshout the page or
 *  compete with the page's own solid primary button (operator review,
 *  23 Oct). Icon-only with an accessible name when collapsed. */
function AppPrimaryActionButton({
  action,
  collapsed,
  onNavigate,
}: {
  action: AppPrimaryAction;
  collapsed: boolean;
  onNavigate?: () => void;
}): ReactNode {
  return (
    <Link
      to={action.to}
      onClick={onNavigate}
      aria-label={collapsed ? action.label : undefined}
      title={collapsed ? action.label : undefined}
      className={`mb-2 flex items-center justify-center gap-1.5 rounded-md border border-primary/30 bg-primary/10 text-sm font-medium text-primary transition-colors hover:border-primary hover:bg-primary hover:text-primary-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-1 ${
        collapsed ? 'mx-auto h-8 w-8' : 'mx-1.5 h-8 px-3'
      }`}
    >
      {renderIcon(action.iconKey, 'h-4 w-4 shrink-0')}
      {!collapsed && <span className="truncate">{action.label}</span>}
    </Link>
  );
}

function toNavSection(
  section: AppSidebarSection,
  badges: Record<string, number> = {},
  previewKeys: ReadonlySet<string> = new Set(),
): NavSection {
  return {
    title: section.title,
    items: section.items.map((it) => toNavItem(it, badges, previewKeys)),
  };
}

// MP114 — MSSP nav promoted from a single appended item in the
// Organization section to its own dedicated section. Discoverable
// for MSSP admins (the cross-client surfaces are the headline value
// of MSSP, not a buried sub-item) and extensible — future cross-
// client surfaces (multi-tenant audit, multi-tenant policy roll-up,
// etc.) can land here without restructuring.
//
// Renders only when the active org's type === 'mssp' AND the mssp
// entitlement is enabled. The single existing item is "Clients" →
// the cross-client overview page (MP35-38 + MP114 risk roll-up);
// future surfaces add as siblings.
const MSSP_SECTION_ITEMS: NavItem[] = [
  {
    to: '/shell/clients',
    label: 'Clients overview',
    icon: <Building2 className="h-4 w-4" />,
  },
];

// MP124 — Platform section moved into TENANT_SIDEBAR + consolidated
// to a single row. The constant that used to live here is gone;
// AppLayout's dynamicSections appends TENANT_SIDEBAR.platform
// conditionally based on the platform_superadmin claim.

interface OrgDoc {
  id: string;
  type: string;
  name: string;
  status: string;
  branding?: {
    siteTitle?: string;
    logoUrl?: string;
    primaryColor?: string;
  };
}

/** "#1f2937" -> "240 5.9% 10%". Returns the HSL triple our token system
 *  uses (CSS vars defined as `H S% L%`, then consumed via
 *  `hsl(var(--primary))`). Keeps a single substitution path so existing
 *  Tailwind utilities react automatically. */
function hexToHslTriple(hex: string): string | null {
  const m = hex.replace('#', '');
  const expanded = m.length === 3 ? m.split('').map((c) => c + c).join('') : m;
  if (!/^[0-9a-fA-F]{6}$/.test(expanded)) return null;
  const r = parseInt(expanded.slice(0, 2), 16) / 255;
  const g = parseInt(expanded.slice(2, 4), 16) / 255;
  const b = parseInt(expanded.slice(4, 6), 16) / 255;
  const max = Math.max(r, g, b);
  const min = Math.min(r, g, b);
  let h = 0;
  let s = 0;
  const l = (max + min) / 2;
  if (max !== min) {
    const d = max - min;
    s = l > 0.5 ? d / (2 - max - min) : d / (max + min);
    switch (max) {
      case r: h = (g - b) / d + (g < b ? 6 : 0); break;
      case g: h = (b - r) / d + 2; break;
      case b: h = (r - g) / d + 4; break;
    }
    h /= 6;
  }
  return `${Math.round(h * 360)} ${(s * 100).toFixed(1)}% ${(l * 100).toFixed(1)}%`;
}

function initials(input: string | null | undefined): string {
  if (!input) return '?';
  const parts = input.trim().split(/[\s@.]+/).filter(Boolean);
  if (parts.length === 0) return '?';
  if (parts.length === 1) return parts[0].slice(0, 2).toUpperCase();
  return (parts[0][0] + parts[1][0]).toUpperCase();
}

/** MP306 — Pending-approvals count for the sidebar badge. Polls every
 *  30s; auto-refresh + jitter would be nicer but 30s is plenty for
 *  what's usually a low-volume queue. Zero for members without
 *  owner/admin role — the Approvals row is admin-gated at the route
 *  layer already, so a member won't see it. */
function usePendingApprovalsCount(): number {
  const { session } = useAuth();
  const [count, setCount] = useState(0);
  const orgId = session?.activeOrgId ?? null;
  const isAdmin = !!(
    session?.orgRoles &&
    (session.orgRoles.includes('owner') || session.orgRoles.includes('admin'))
  );
  useEffect(() => {
    if (!session || !orgId || !isAdmin) {
      setCount(0);
      return;
    }
    let cancelled = false;
    const refresh = async () => {
      try {
        const idToken = await session.user.getIdToken();
        const approvals = await listAgentApprovals(idToken, orgId, {
          status: 'pending',
          limit: 100,
        });
        if (!cancelled) setCount(approvals.length);
      } catch {
        // Non-fatal — a transient failure just means the badge stays
        // stale until the next tick. Don't spam a toast.
      }
    };
    void refresh();
    const t = setInterval(refresh, 30_000);
    return () => {
      cancelled = true;
      clearInterval(t);
    };
  }, [session, orgId, isAdmin]);
  return count;
}

interface Props {
  title: string;
  children: ReactNode;
  rightSlot?: ReactNode;
  /** Page-level view controls shown right after the page title in the top
   *  bar (Security overview's Focus). The top bar stays visible while the
   *  page scrolls, unlike a page banner. */
  titleSlot?: ReactNode;
}

export function AppLayout({ title, children, rightSlot, titleSlot }: Props) {
  const { session, signOut, switchOrg } = useAuth();
  const { memberships } = useMemberships();
  // MP306 — Pending-approvals count for the sidebar Approvals badge.
  // Polls every 30s so an admin sees new pending approvals without
  // waiting for MP304's email. Only fires for tenants where the
  // caller has owner/admin role; regular members never see the
  // Approvals row.
  const approvalsBadge = usePendingApprovalsCount();
  const { data: entitlementData } = useOrgEntitlements();
  const auditEntitled = useEntitlement('audit');
  const aiInsightsEntitled = useEntitlement('aiInsights');
  // MP405 — the Orchestrate shortcut in the overview group follows the
  // AI Insights two-layer rule: entitlement here, app flag via the
  // row's `featureFlag` in apps.ts.
  const orchestrateEntitled = useEntitlement('app.orchestrate');
  // MP316c — Resolved feature-flag set for this org. Any nav item
  // whose `featureFlag` key exists AND resolves to enabled=false is
  // filtered from the sidebar. Missing keys pass through — same
  // "hide when explicitly disabled" pattern used by the launcher for
  // app-level flags (LauncherPage.tsx `hiddenAppKeys`).
  const { flags: resolvedFlags } = useAllFeatureFlags();
  const flagDisabledKeys = useMemo(() => {
    const s = new Set<string>();
    for (const f of resolvedFlags) {
      if (!f.enabled) s.add(f.key);
    }
    return s;
  }, [resolvedFlags]);
  // MP316i — keys this org has via a beta channel (opt-in or ramp).
  // Rows whose `featureFlag` is in here get the Preview chip.
  const flagPreviewKeys = useMemo(() => {
    const s = new Set<string>();
    for (const f of resolvedFlags) {
      if (f.enabled && f.isBeta) s.add(f.key);
    }
    return s;
  }, [resolvedFlags]);
  // Owners and admins see rows marked `adminOnly` (an app's Settings);
  // everyone else never does.
  const isOrgAdmin = !!(session?.orgRoles?.includes('owner') || session?.orgRoles?.includes('admin'));
  const isItemFlagHidden = useCallback(
    (it: AppSidebarItem): boolean =>
      (!!it.featureFlag && flagDisabledKeys.has(it.featureFlag)) || (!!it.adminOnly && !isOrgAdmin),
    [flagDisabledKeys, isOrgAdmin],
  );
  const filterFlagged = useCallback(
    (items: AppSidebarItem[]): AppSidebarItem[] => {
      // Recursively drop children whose flag is disabled so a gated
      // sub-item disappears from the parent's accordion too.
      return items
        .filter((it) => !isItemFlagHidden(it))
        .map((it) =>
          it.children
            ? { ...it, children: filterFlagged(it.children) }
            : it,
        );
    },
    [isItemFlagHidden],
  );
  const whiteLabelEntitled = useEntitlement('whiteLabel');
  const msspEntitled = useEntitlement('mssp');
  const navigate = useNavigate();
  const location = useLocation();
  // MP738 — the standalone product this host serves, or null on the
  // suite. Fixed for the page load (the hostname cannot change without
  // a reload). Every standalone difference below keys off this one value.
  const standalone = currentStandaloneBrand();
  const goToTargets = useMemo(() => goToTargetsFor(standalone?.entryRoute ?? null), [standalone]);
  // Athena opens in a panel beside the page (AthenaPanel). Same gate
  // as before: owners and admins, unless the athena.copilot flag is
  // explicitly off. The full page shows the conversation itself, so
  // the panel stays closed there.
  const athena = useOptionalAthena();
  const athenaAllowed =
    !!athena &&
    !!(session?.orgRoles?.includes('owner') || session?.orgRoles?.includes('admin')) &&
    !flagDisabledKeys.has('athena.copilot');
  const onAthenaPage = location.pathname === '/shell/copilot';
  const athenaPanelOpen = athenaAllowed && !onAthenaPage && !!athena?.panelOpen;
  // Ctrl/⌘ + / opens and closes the panel from anywhere in the shell.
  useEffect(() => {
    if (!athenaAllowed || onAthenaPage) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === '/' && (e.metaKey || e.ctrlKey) && !e.altKey) {
        e.preventDefault();
        athena?.setPanelOpen(!athena.panelOpen);
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [athenaAllowed, onAthenaPage, athena]);
  const [org, setOrg] = useState<OrgDoc | null>(null);
  const [switching, setSwitching] = useState<string | null>(null);
  const [switchError, setSwitchError] = useState<string | null>(null);
  // Mobile drawer state. The sidebar is hidden by default below md (Sidebar
  // primitive enforces this); flipping mobileNavOpen slides it in via a
  // fixed-position overlay. Auto-closes on every route change so a tap on a
  // nav link doesn't leave the drawer dangling open.
  const [mobileNavOpen, setMobileNavOpen] = useState(false);
  // Below 768px the secondary asides are hidden, so the drawer must carry
  // the current app's menu and expand groups in place (menu review, Oct 2026).
  const [isDesktop, setIsDesktop] = useState<boolean>(() =>
    typeof window === 'undefined' || !window.matchMedia ? true : window.matchMedia('(min-width: 768px)').matches,
  );
  useEffect(() => {
    if (typeof window === 'undefined' || !window.matchMedia) return undefined;
    const mq = window.matchMedia('(min-width: 768px)');
    const onChange = () => setIsDesktop(mq.matches);
    mq.addEventListener('change', onChange);
    return () => mq.removeEventListener('change', onChange);
  }, []);
  useEffect(() => {
    setMobileNavOpen(false);
  }, [location.pathname]);

  // Desktop collapse — separate concern from the mobile drawer. Initial
  // value reads from localStorage so the user's preference survives reloads;
  // we default to expanded for first-time users so the nav is discoverable.
  // Mobile is unaffected by this (drawer slides in over content, no
  // intermediate "icons-only" state).
  // MP122 — Apps aside open/closed state. Transient drawer (not
  // persisted) — clicking the Apps row in the main sidebar flips
  // it; any subsequent navigation (route change) closes it.
  // Auto-close on pathname change handles both: (1) clicking an
  // app in the aside enters an app + closes the aside so the
  // per-app aside takes over without doubling up; (2) clicking a
  // different main-nav item (Home, Identity, etc.) also closes
  // the aside since the user moved on.
  const [appsAsideOpen, setAppsAsideOpen] = useState<boolean>(false);

  // MP60 — User's manual collapse state for sub-menu parent rows
  // (keyed by the parent's `to`). Overrides the URL-derived
  // auto-expansion in NavRow. Persisted to localStorage so user
  // preferences survive reloads. Stored as a JSON object map; the
  // in-memory shape is a Map for ergonomic NavRow lookups.
  const SUBMENU_OVERRIDE_KEY = 'xone.sidebarSubmenuOverrides';
  const [submenuOverride, setSubmenuOverride] = useState<Map<string, boolean>>(() => {
    if (typeof window === 'undefined') return new Map();
    try {
      const raw = window.localStorage.getItem(SUBMENU_OVERRIDE_KEY);
      if (!raw) return new Map();
      const obj = JSON.parse(raw) as Record<string, boolean>;
      return new Map(Object.entries(obj));
    } catch {
      return new Map();
    }
  });
  const onToggleSubmenu = (parentTo: string, nextOpen: boolean) => {
    setSubmenuOverride((prev) => {
      const next = new Map(prev);
      next.set(parentTo, nextOpen);
      if (typeof window !== 'undefined') {
        window.localStorage.setItem(SUBMENU_OVERRIDE_KEY, JSON.stringify(Object.fromEntries(next)));
      }
      return next;
    });
  };

  // MP120 — Hover-preview state for the icon-only primary sidebar.
  // When the user hovers an icon, we surface its secondary nav as
  // a transient overlay panel (ClickUp pattern) so they can glance
  // without committing to a click. Clicking still navigates, and
  // that navigation re-renders the pinned secondary aside via the
  // existing URL-match path — so "click pins" requires no extra
  // state.
  //
  // Enter/leave timers prevent flicker as the mouse crosses the
  // small gap between icon → overlay. Refs are used (not state) so
  // the timers don't trigger re-renders on every cursor twitch.
  type HoverPreview =
    | { kind: 'app'; app: AppDefinition }
    | { kind: 'tenant'; item: NavItem }
    | null;
  const [hoverPreview, setHoverPreview] = useState<HoverPreview>(null);
  const enterTimerRef = useRef<number | null>(null);
  const leaveTimerRef = useRef<number | null>(null);
  const showPreviewSoon = (next: NonNullable<HoverPreview>) => {
    if (leaveTimerRef.current !== null) {
      window.clearTimeout(leaveTimerRef.current);
      leaveTimerRef.current = null;
    }
    if (enterTimerRef.current !== null) window.clearTimeout(enterTimerRef.current);
    enterTimerRef.current = window.setTimeout(() => setHoverPreview(next), 80);
  };
  const clearPreviewSoon = () => {
    if (enterTimerRef.current !== null) {
      window.clearTimeout(enterTimerRef.current);
      enterTimerRef.current = null;
    }
    if (leaveTimerRef.current !== null) window.clearTimeout(leaveTimerRef.current);
    leaveTimerRef.current = window.setTimeout(() => setHoverPreview(null), 220);
  };
  const cancelPreviewLeave = () => {
    if (leaveTimerRef.current !== null) {
      window.clearTimeout(leaveTimerRef.current);
      leaveTimerRef.current = null;
    }
  };
  useEffect(() => {
    return () => {
      if (enterTimerRef.current !== null) window.clearTimeout(enterTimerRef.current);
      if (leaveTimerRef.current !== null) window.clearTimeout(leaveTimerRef.current);
    };
  }, []);
  // Dismiss on route change — once a click navigates, the pinned
  // secondary aside takes over and the overlay would otherwise
  // briefly double up.
  useEffect(() => {
    setHoverPreview(null);
  }, [location.pathname, location.hash]);

  const [collapsed, setCollapsed] = useState<boolean>(() => {
    if (typeof window === 'undefined') return false;
    return window.localStorage.getItem('xone.sidebar.collapsed') === '1';
  });
  useEffect(() => {
    if (typeof window === 'undefined') return;
    window.localStorage.setItem('xone.sidebar.collapsed', collapsed ? '1' : '0');
  }, [collapsed]);

  // MP68 — Secondary aside (per-app + per-tenant-section) gets
  // its own collapse state so users can independently shrink the
  // secondary panel when they want more room for content but
  // still need the main sidebar expanded (or vice versa).
  const [secondaryCollapsed, setSecondaryCollapsed] = useState<boolean>(() => {
    if (typeof window === 'undefined') return false;
    return window.localStorage.getItem('xone.sidebar.secondary.collapsed') === '1';
  });
  useEffect(() => {
    if (typeof window === 'undefined') return;
    window.localStorage.setItem(
      'xone.sidebar.secondary.collapsed',
      secondaryCollapsed ? '1' : '0',
    );
  }, [secondaryCollapsed]);

  useEffect(() => {
    if (!session?.activeOrgId || !db) return;
    let cancelled = false;
    (async () => {
      try {
        const snap = await getDoc(doc(db, 'organizations', session.activeOrgId!));
        if (cancelled || !snap.exists()) return;
        setOrg({ id: snap.id, ...snap.data() } as OrgDoc);
      } catch {
        // ignored — header still renders without org name
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [session?.activeOrgId]);

  const handleSignOut = async () => {
    await signOut();
    navigate('/signin', { replace: true });
  };

  const handleSwitch = async (orgId: string) => {
    if (orgId === session?.activeOrgId) return;
    setSwitchError(null);
    setSwitching(orgId);
    try {
      await switchOrg(orgId);
      setOrg(null); // forces the org effect to refetch
    } catch (err) {
      setSwitchError(err instanceof Error ? err.message : String(err));
    } finally {
      setSwitching(null);
    }
  };

  // An organization's own white-label title wins; on a standalone host
  // the product's name replaces the suite's.
  const siteTitle = org?.branding?.siteTitle ?? standalone?.productName ?? 'X-One Cyber Aware';
  const logoUrl = org?.branding?.logoUrl;
  const primaryHsl = org?.branding?.primaryColor
    ? hexToHslTriple(org.branding.primaryColor)
    : null;
  const cssVarStyle: React.CSSProperties | undefined = primaryHsl
    ? ({ ['--primary' as string]: primaryHsl } as React.CSSProperties)
    : undefined;

  // Resolve which app context the current URL belongs to. App URLs
  // (/aware/*, /xes, /posture, ...) return the corresponding app.
  // Tenant-wide URLs (/identity/*, /audit, /settings, /risk, ...)
  // return null — but we still want to show *some* app's sidebar so
  // tenant pages don't feel like a dead end. `effectiveApp` below
  // falls through to the last-visited app (persisted across loads
  // via localStorage) or the first entitled app.
  const currentApp: AppDefinition | null = useMemo(
    () => resolveAppFromPathname(location.pathname),
    [location.pathname],
  );

  // MP122 — Close the Apps aside on any subsequent route change.
  // Skips the initial mount so opening the aside (which doesn't
  // change the route) isn't immediately undone, and so a fresh
  // mount on /aware/* doesn't fight a localStorage-restored open
  // state (we don't persist anymore, but the skip is harmless and
  // covers future restore mechanisms). Pathname change covers
  // both flows the user wants closed: picking an app from the
  // aside (route → /aware/*) and clicking another main-nav row.
  const appsAsideMountedRef = useRef(false);
  useEffect(() => {
    if (!appsAsideMountedRef.current) {
      appsAsideMountedRef.current = true;
      return;
    }
    setAppsAsideOpen(false);
  }, [location.pathname]);

  // Apps entitled for this org — drives the app switcher dropdown in
  // the header. Locked apps don't show up; clicking the launcher icon
  // is the way to see what they're paying for. "Coming soon" apps with
  // a null entryRoute are excluded — they aren't switchable yet.
  //
  // MP801 follow-up — EXTERNAL apps (none since ADR 0030 moved the AI Gateway in) are excluded from
  // `entitledApps`, and the `!a.external` guard says so explicitly
  // rather than leaning on their null entryRoute: this list feeds
  // `effectiveApp`, so the sidebar can never fall back to an app with
  // an empty `sidebar` array. The original decision (2026-09) also kept
  // external apps out of the Apps aside entirely — "a new-tab jump
  // inside a switcher whose every other row is an in-place route
  // change would be a false affordance".
  //
  // Revised 2026-09-13 (operator): the Apps aside and the launcher
  // must show the SAME set of entitled apps, or a tester inside
  // Resilience finds a different suite than the one they were sold.
  // External apps now render in their own "External products" group
  // BELOW the in-shell list — plain anchors, new tab, ↗ icon — so the
  // affordance is honest without touching `entitledApps`.
  //
  // MP738 — on a standalone host the list is the product alone, whatever
  // else the organization holds: its menu and palette entries always
  // show (its pages explain when the plan does not include it), and no
  // other app is listed anywhere. No external products either.
  const entitledApps = useMemo(() => {
    if (standalone) return appsForHost(APPS, standalone.app);
    const map = entitlementData?.entitlements ?? {};
    return APPS.filter((a) => !a.external && a.entryRoute && Boolean(map[a.entitlementKey]));
  }, [entitlementData, standalone]);
  const externalApps = useMemo(() => {
    if (standalone) return [];
    const map = entitlementData?.entitlements ?? {};
    return APPS.filter((a) => a.external === true && !!a.externalUrl && Boolean(map[a.entitlementKey]));
  }, [entitlementData, standalone]);

  // Persist the last app key the user actually navigated into. We
  // read it back when rendering tenant pages so the per-app sidebar
  // stays visible — fixes the post-MP7 regression where switching
  // from /aware/* to /risk made the Aware nav vanish.
  const LAST_APP_KEY = 'xone.lastVisitedApp';
  const [lastVisitedAppKey, setLastVisitedAppKey] = useState<AppKey | null>(() => {
    if (typeof window === 'undefined') return null;
    const stored = window.localStorage.getItem(LAST_APP_KEY);
    return stored && APPS.some((a) => a.key === stored) ? (stored as AppKey) : null;
  });
  useEffect(() => {
    if (currentApp) {
      setLastVisitedAppKey(currentApp.key);
      if (typeof window !== 'undefined') {
        window.localStorage.setItem(LAST_APP_KEY, currentApp.key);
      }
    }
  }, [currentApp]);

  // MP89 — Scroll to top on every pathname change. SPA nav
  // otherwise keeps the previous scroll offset, so clicking a
  // sidebar item while scrolled down lands you on the new page
  // mid-fold and the page looks "broken" because the hero is
  // above the visible area.
  //
  // We hit every plausible scroll container: the AppContent ref
  // (the *intended* scroll container per the layout), window
  // (in case the body is the actual scroll container — happens
  // on layouts where AppMain's overflow-hidden gets bypassed),
  // and document.scrollingElement (browser-quirk fallback).
  //
  // Also runs once immediately + once on the next frame, because
  // some pages do their own scroll work on mount (e.g. focusing
  // an input) and we want to win the last-write race.
  const appContentRef = useRef<HTMLDivElement | null>(null);
  useEffect(() => {
    const reset = () => {
      appContentRef.current?.scrollTo(0, 0);
      try {
        window.scrollTo(0, 0);
      } catch {
        /* SSR / jsdom — ignore */
      }
      const root = document.scrollingElement;
      if (root) root.scrollTop = 0;
    };
    reset();
    const raf = requestAnimationFrame(reset);
    return () => cancelAnimationFrame(raf);
  }, [location.pathname]);

  // MP67 — Walk every tenant section for an item with `children`
  // whose path matches the current URL. When found and we're not
  // inside an app (the per-app secondary sidebar takes priority),
  // render those children in a tenant secondary sidebar to the
  // right of the main one — matches the per-app shape so the
  // affordance is consistent across the product.
  // The Dashboards row under Insights lists each dashboard (pinned
  // first) as its own sub-menu; the items depend on the person's pins,
  // saved KPI dashboards and access, so they are filled in here.
  // Likewise the top-level Policies row lists each rule set the org has
  // (lib/usePolicyMenu.ts).
  const dashboardMenu = useDashboardMenu();
  const policyMenu = usePolicyMenu();
  // And the Configuration row lists the hub's cards this person can use,
  // under their categories, by the hub's own rule (lib/configurationMenu.ts).
  const configurationMenu = useMemo(
    () =>
      configurationMenuItems({
        isAdmin: isOrgAdmin,
        entitlements: entitlementData?.entitlements ?? null,
        disabledFlags: flagDisabledKeys,
      }),
    [isOrgAdmin, entitlementData, flagDisabledKeys],
  );
  const tenantSidebar = useMemo(() => {
    const fill = (items: AppSidebarItem[]): AppSidebarItem[] =>
      items.map((it) =>
        it.to === '/dashboards' && !it.children && dashboardMenu
          ? { ...it, children: dashboardMenu }
          : it.to === '/policies' && it.label === 'Policies'
            ? { ...it, children: policyMenu }
            : it.to === '/configuration' && it.label === 'Configuration'
              ? { ...it, children: configurationMenu }
              : it.children
                ? { ...it, children: fill(it.children) }
                : it,
      );
    const suite: TenantSidebarConfig = { ...TENANT_SIDEBAR, insights: { ...TENANT_SIDEBAR.insights, items: fill(TENANT_SIDEBAR.insights.items) } };
    if (!standalone) return suite;
    // MP738 — standalone host: the top row opens the product (there is
    // no launcher), and the sections that span every app are emptied
    // (their routes redirect too, in StandaloneRouteGuard). People,
    // integrations, audit, settings and plan stay.
    const out: TenantSidebarConfig = {
      ...suite,
      overview: {
        title: null,
        items: [{ to: standalone.entryRoute, label: standalone.shortName, iconKey: standalone.iconKey }],
      },
    };
    for (const key of STANDALONE_HIDDEN_TENANT_SECTIONS) out[key] = { ...out[key], items: [] };
    return out;
  }, [dashboardMenu, policyMenu, configurationMenu, standalone]);

  const activeTenantItem: AppSidebarItem | null = useMemo(() => {
    if (currentApp) return null;
    // MP121 — Also match if any (recursive) child's path matches.
    // The consolidated Reporting / Identity / Integrations rows
    // each set the parent's `to` to the FIRST child's route as a
    // landing target, but the user may navigate among siblings
    // (e.g. /reports, /kpi) — the aside should stay open across
    // all of them, not just the landing route.
    const matchesPath = (path: string): boolean => {
      const clean = path.split('#')[0];
      return location.pathname === clean || location.pathname.startsWith(clean + '/');
    };
    const anyChildMatches = (children: AppSidebarItem[]): boolean => {
      for (const child of children) {
        if (child.shortcut) continue;
        if (matchesPath(child.to)) return true;
        if (child.children && anyChildMatches(child.children)) return true;
      }
      return false;
    };
    for (const section of Object.values(tenantSidebar)) {
      for (const item of section.items) {
        if (!item.children || item.children.length === 0) continue;
        if (matchesPath(item.to) || anyChildMatches(item.children)) return item;
      }
    }
    return null;
  }, [currentApp, location.pathname, tenantSidebar]);

  // Sidebar app pick — current app when on /aware/* etc., otherwise
  // the last app the user actually visited, otherwise the first
  // entitled app (Aware for most orgs). Only "shipped" apps qualify
  // — we never show a sidebar for an app with no entryRoute.
  const effectiveApp: AppDefinition | null = useMemo(() => {
    if (currentApp) return currentApp;
    if (lastVisitedAppKey) {
      const remembered = getApp(lastVisitedAppKey);
      if (remembered?.entryRoute) return remembered;
    }
    return entitledApps[0] ?? APPS.find((a) => a.entryRoute) ?? null;
  }, [currentApp, lastVisitedAppKey, entitledApps]);

  // Build the sidebar sections dynamically. Per-app items used to
  // render here at the top, but MP55 promoted them to a separate
  // secondary sidebar column (rendered between this primary
  // sidebar and the page content). The MP51 isLauncher gate is
  // therefore moot for the main sidebar but kept defensively in
  // case the per-app section ever re-enters the main column.
  const isLauncher = location.pathname === '/launcher';
  // MP306 — Sidebar badge map keyed by route path. Extending: add a
  // new count-hook, drop the value in here, and toNavItem/toNavSection
  // automatically pick it up. Only Approvals today.
  const sidebarBadges: Record<string, number> = useMemo(
    () => ({
      '/agents/approvals': approvalsBadge,
    }),
    [approvalsBadge],
  );
  // Menu review (Oct 2026) — ONE gate for every organization-wide row,
  // applied to the main sidebar AND the tenant aside. Rows now sit inside
  // groups (AI Insights in Insights, Audit log in Organization), and the
  // aside renders a group's children directly, so gating only the main
  // sidebar would let a gated row surface in the aside.
  //   flag off                         → hidden (filterFlagged)
  //   /insights without aiInsights     → hidden
  //   /audit without audit             → hidden
  //   /orchestrate without the app     → hidden (never a dead link)
  // A parent left with no visible children is dropped.
  const gateTenantItems = useCallback(
    (items: AppSidebarItem[]): AppSidebarItem[] => {
      const allowed = (it: AppSidebarItem): boolean => {
        if (it.to === '/insights') return aiInsightsEntitled;
        if (it.to === '/audit') return auditEntitled;
        if (it.to === '/orchestrate') return orchestrateEntitled;
        return true;
      };
      const walk = (xs: AppSidebarItem[]): AppSidebarItem[] =>
        xs
          .filter(allowed)
          .map((it) => (it.children ? { ...it, children: walk(it.children) } : it))
          .filter((it) => !it.children || it.children.length > 0);
      return walk(filterFlagged(items));
    },
    [filterFlagged, aiInsightsEntitled, auditEntitled, orchestrateEntitled],
  );

  // The current app's create action: admins only (when marked), and hidden
  // when its flag is explicitly off — the same rules as menu rows.
  const appPrimaryAction: AppPrimaryAction | null = (() => {
    const a = currentApp?.primaryAction;
    if (!a) return null;
    if (a.adminOnly && !isOrgAdmin) return null;
    if (a.featureFlag && flagDisabledKeys.has(a.featureFlag)) return null;
    return a;
  })();

  // The tenant aside shows the active group's children through the same gate.
  const tenantAsideChildren: AppSidebarItem[] = useMemo(
    () => (activeTenantItem?.children ? gateTenantItems(activeTenantItem.children) : []),
    [activeTenantItem, gateTenantItems],
  );

  const dynamicSections: SidebarEntry[] = useMemo(() => {
    const sections: SidebarEntry[] = [];
    const push = (section: AppSidebarSection) => {
      const items = gateTenantItems(section.items);
      if (items.length > 0) sections.push(toNavSection({ ...section, items }, sidebarBadges, flagPreviewKeys));
    };
    // Home, then the Apps toggle (MP122 marker, swapped for a button by
    // the render loop), then Insights and the Administration group.
    push(tenantSidebar.overview);
    // No Apps panel on a standalone host: there is one product.
    if (entitledApps.length > 0 && !standalone) {
      sections.push({ kind: 'apps-toggle' });
    }
    push(tenantSidebar.insights);
    push(tenantSidebar.identity);
    push(tenantSidebar.integrations);
    push(tenantSidebar.organization);
    // MP114 — MSSP section, between Organization and Help.
    if (org?.type === 'mssp' && msspEntitled) {
      sections.push({ title: 'MSSP', items: MSSP_SECTION_ITEMS });
    }
    // MP124 — Platform row (superadmin-only), just above Help.
    if (session?.platformRoles.includes('platform_superadmin')) {
      push(tenantSidebar.platform);
    }
    return sections;
  }, [gateTenantItems, org?.type, msspEntitled, session?.platformRoles, sidebarBadges, flagPreviewKeys, entitledApps.length, tenantSidebar, standalone]);

  // ── Command palette (Ctrl+K / ⌘K / "/") ──────────────────────────
  // Built from the SAME filtered data the sidebars render: each
  // entitled app's sidebar (flag-filtered), then the tenant sections
  // already filtered by entitlement and flag above. A page the sidebar
  // hides can never appear here. The current app goes first so its
  // pages win ties and keep their app context.
  const [paletteOpen, setPaletteOpen] = useState(false);
  const [paletteRecents, setPaletteRecents] = useState<string[]>(() => {
    try {
      const raw = window.localStorage.getItem(PALETTE_RECENTS_KEY);
      const parsed: unknown = raw ? JSON.parse(raw) : [];
      return Array.isArray(parsed) ? parsed.filter((x): x is string => typeof x === 'string').slice(0, 6) : [];
    } catch {
      return [];
    }
  });
  const paletteFocusAppKeys = useFocusAppKeys(paletteOpen);
  const paletteEntries = useMemo(() => {
    type Row = { to: string; label: string; shortcut?: boolean; children?: Row[] };
    const toSource = (items: Row[]): PaletteSourceItem[] =>
      items.map((it) => ({
        to: it.to,
        label: it.label,
        shortcut: it.shortcut,
        children: it.children ? toSource(it.children) : undefined,
      }));
    const sources: PaletteSource[] = [];
    const appsInOrder = currentApp
      ? [currentApp, ...entitledApps.filter((a) => a.key !== currentApp.key)]
      : entitledApps;
    for (const app of appsInOrder) {
      if (!entitledApps.some((a) => a.key === app.key)) continue;
      for (const section of app.sidebar) {
        sources.push({ group: app.shortName, section: section.title, appKey: app.key, items: toSource(filterFlagged(section.items)) });
      }
    }
    for (const entry of dynamicSections) {
      if (isAppsToggleMarker(entry)) continue;
      sources.push({ group: entry.title ?? 'Home', items: toSource(entry.items) });
    }
    return buildPaletteEntries(sources);
  }, [currentApp, entitledApps, filterFlagged, dynamicSections]);

  // Go-to sequences (G then a letter) and the "?" shortcuts panel.
  // The handler below is registered once, so it reaches navigate and
  // the Apps panel through refs.
  const [goPending, setGoPending] = useState(false);
  const [shortcutsOpen, setShortcutsOpen] = useState(false);
  const goStateRef = useRef<GoToState>(null);
  const goTimerRef = useRef<number | null>(null);
  const navigateRef = useRef(navigate);
  navigateRef.current = navigate;
  useEffect(
    () => () => {
      if (goTimerRef.current !== null) window.clearTimeout(goTimerRef.current);
    },
    [],
  );

  // Global shortcuts. Ctrl/⌘+K toggles from anywhere; "/" opens only
  // when the user is not typing in a field.
  useEffect(() => {
    const endGo = () => {
      goStateRef.current = null;
      setGoPending(false);
      if (goTimerRef.current !== null) window.clearTimeout(goTimerRef.current);
      goTimerRef.current = null;
    };
    const onKey = (e: KeyboardEvent) => {
      if ((e.metaKey || e.ctrlKey) && !e.altKey && e.key.toLowerCase() === 'k') {
        e.preventDefault();
        endGo();
        setPaletteOpen((o) => !o);
        return;
      }
      const typing = isTypingTarget(e.target as HTMLElement | null);
      if (e.key === '?' && !typing && !e.metaKey && !e.ctrlKey && !e.altKey) {
        e.preventDefault();
        endGo();
        setShortcutsOpen(true);
        return;
      }
      if (e.key === 'Shift') return;
      const step = goToStep(
        goStateRef.current,
        e.key,
        { ctrl: e.ctrlKey, meta: e.metaKey, alt: e.altKey },
        typing,
        Date.now(),
        goToTargets,
      );
      if (step.kind === 'start') {
        e.preventDefault();
        goStateRef.current = { pendingSince: Date.now() };
        setGoPending(true);
        if (goTimerRef.current !== null) window.clearTimeout(goTimerRef.current);
        goTimerRef.current = window.setTimeout(endGo, GO_TO_WINDOW_MS);
        return;
      }
      if (step.kind === 'cancel') endGo();
      if (step.kind === 'go') {
        e.preventDefault();
        endGo();
        if (step.target.action === 'apps') setAppsAsideOpen(true);
        else if (step.target.to) navigateRef.current(step.target.to);
        return;
      }
      if (e.key === '/' && !e.metaKey && !e.ctrlKey && !e.altKey && !isTypingTarget(e.target as HTMLElement | null)) {
        e.preventDefault();
        setPaletteOpen(true);
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [goToTargets]);

  // Recents follow real visits, not just palette use, so the list is
  // useful the first time it opens.
  useEffect(() => {
    const here = `${location.pathname}${location.hash}`;
    const hit = paletteEntries.find((e) => e.to === here) ?? paletteEntries.find((e) => e.to === location.pathname);
    if (!hit) return;
    setPaletteRecents((prev) => {
      if (prev[0] === hit.to) return prev;
      const next = pushRecent(prev, hit.to);
      try {
        window.localStorage.setItem(PALETTE_RECENTS_KEY, JSON.stringify(next));
      } catch {
        /* private mode / blocked storage: recents just are not remembered */
      }
      return next;
    });
  }, [location.pathname, location.hash, paletteEntries]);

  // Every tab gets a real title: "Campaigns · Aware · X-One Cyber Aware".
  useEffect(() => {
    document.title = documentTitleFor(title, currentApp?.shortName, siteTitle);
  }, [title, currentApp?.shortName, siteTitle]);

  // Mobile drawer: Escape closes it and focus returns to the button
  // that opened it.
  const mobileNavButtonRef = useRef<HTMLButtonElement | null>(null);
  useEffect(() => {
    if (!mobileNavOpen) return undefined;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        setMobileNavOpen(false);
        mobileNavButtonRef.current?.focus();
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [mobileNavOpen]);

  return (
    <AppShell style={cssVarStyle} className="relative">
      {/* Skip link: the first tab stop on every page. Hidden until
          focused, then jumps past both sidebars to the page content. */}
      <a
        href="#main-content"
        className="sr-only focus:not-sr-only focus:fixed focus:left-3 focus:top-3 focus:z-[70] focus:rounded-md focus:bg-indigo-600 focus:px-4 focus:py-2 focus:text-sm focus:font-semibold focus:text-white focus:shadow-lg"
      >
        Skip to main content
      </a>
      {goPending && <GoToHint targets={goToTargets} />}
      {shortcutsOpen && <ShortcutsDialog targets={goToTargets} onClose={() => setShortcutsOpen(false)} />}
      <CommandPalette
        open={paletteOpen}
        onClose={() => setPaletteOpen(false)}
        entries={paletteEntries}
        recents={paletteRecents}
        focusAppKeys={paletteFocusAppKeys}
        onNavigate={(to) => navigate(to)}
      />
      {/* Mobile drawer backdrop — visible only when drawer is open + below md.
          Tapping it closes the drawer. */}
      {mobileNavOpen && (
        <div
          onClick={() => setMobileNavOpen(false)}
          className="fixed inset-0 z-40 bg-background/70 backdrop-blur-sm md:hidden"
          aria-hidden="true"
        />
      )}
      <Sidebar
        className={`fixed inset-y-0 left-0 z-50 flex w-60 transform transition-[transform,width] duration-200 md:relative md:translate-x-0 ${
          mobileNavOpen ? 'translate-x-0' : '-translate-x-full'
        } ${collapsed ? 'md:w-20' : 'md:w-52'}`}
      >
        <SidebarHeader className={collapsed ? 'justify-center px-2' : undefined}>
          {/* In collapsed mode the toggle replaces the logo as the sole
              header content — fitting both the 28px logo + 28px toggle +
              gap into the 56px-wide sidebar would clip one of them, and
              losing the expand affordance leaves the user stranded. */}
          {!collapsed && (
            <>
              {logoUrl ? (
                <img
                  src={logoUrl}
                  alt=""
                  className="h-7 w-7 shrink-0 rounded-md object-cover ring-1 ring-border"
                />
              ) : standalone ? (
                // MP738 — the product's own mark on the indigo brand tile.
                <div className="flex h-7 w-7 shrink-0 items-center justify-center rounded-md bg-indigo-600 text-white shadow-sm shadow-indigo-600/30 dark:bg-indigo-500">
                  {renderIcon(standalone.iconKey, 'h-4 w-4 text-white')}
                </div>
              ) : (
                <div className="flex h-7 w-7 shrink-0 items-center justify-center rounded-md bg-gradient-to-br from-blue-600 to-purple-600 shadow-sm shadow-blue-600/30">
                  <ShieldAlert className="h-4 w-4 text-white" />
                </div>
              )}
              <span className="truncate font-semibold tracking-tight">{siteTitle}</span>
            </>
          )}
          {/* Desktop collapse / expand toggle — hidden on mobile, where the
              drawer pattern handles space concerns instead. Sits on the right
              of the header when expanded; sole/centered content when collapsed. */}
          <button
            type="button"
            onClick={() => setCollapsed((c) => !c)}
            className={`hidden h-7 w-7 items-center justify-center rounded-md text-muted-foreground hover:bg-accent hover:text-foreground md:inline-flex ${collapsed ? '' : 'ml-auto'}`}
            aria-label={collapsed ? 'Expand navigation' : 'Collapse navigation'}
            title={collapsed ? 'Expand navigation' : 'Collapse navigation'}
          >
            {collapsed ? <PanelLeftOpen className="h-4 w-4" /> : <PanelLeftClose className="h-4 w-4" />}
          </button>
          {/* Close-drawer button — only meaningful on mobile, hidden md+ */}
          <button
            type="button"
            onClick={() => setMobileNavOpen(false)}
            className="ml-auto inline-flex h-7 w-7 items-center justify-center rounded-md text-muted-foreground hover:bg-accent hover:text-foreground md:hidden"
            aria-label="Close navigation"
          >
            <X className="h-4 w-4" />
          </button>
        </SidebarHeader>
        <SidebarNav aria-label="Main">
          {dynamicSections.map((entry, sIdx) => {
            // MP122 — Apps toggle row. Renders as a button that
            // flips appsAsideOpen (toggle-only, no navigation).
            // Hover handlers run only in collapsed mode and feed
            // the MP120 hover-preview with a synthetic NavItem
            // whose children are the entitled apps.
            if (isAppsToggleMarker(entry)) {
              const synthetic: NavItem = {
                to: '__apps_toggle__',
                label: 'Apps',
                icon: <Grid3x3 className="h-4 w-4" />,
                children: entitledApps.map((app) => ({
                  to: app.entryRoute!,
                  label: app.shortName,
                  icon: renderIcon(app.sidebar[0]?.items[0]?.iconKey ?? 'dashboard'),
                })),
              };
              return (
                <div key={`s-${sIdx}-apps`} className={sIdx > 0 ? 'mt-3' : undefined}>
                  <div
                    onMouseEnter={
                      collapsed || !appsAsideOpen
                        ? () => showPreviewSoon({ kind: 'tenant', item: synthetic })
                        : undefined
                    }
                    onMouseLeave={clearPreviewSoon}
                  >
                    <button
                      type="button"
                      onClick={() => setAppsAsideOpen((o) => !o)}
                      aria-pressed={appsAsideOpen}
                      aria-label={appsAsideOpen ? 'Close Apps' : 'Open Apps'}
                      title={collapsed ? 'Apps' : undefined}
                      className="block w-full text-left"
                    >
                      {collapsed ? (
                        <div
                          className={`group flex flex-col items-center gap-1 rounded-md px-1 py-2 text-sm font-medium text-muted-foreground transition-colors hover:bg-accent hover:text-foreground [&>span:last-child]:line-clamp-2 [&>span:last-child]:whitespace-normal [&>span:last-child]:text-[11px] [&>span:last-child]:hyphens-auto [&>span:last-child]:[overflow-wrap:anywhere] ${
                            appsAsideOpen
                              ? 'relative bg-accent text-foreground before:absolute before:left-0 before:top-2 before:bottom-2 before:w-0.5 before:rounded-r before:bg-primary'
                              : ''
                          }`}
                        >
                          <span
                            className={`flex shrink-0 items-center justify-center transition-colors h-6 w-6 [&>svg]:h-6 [&>svg]:w-6 ${appsAsideOpen ? 'text-primary' : ''}`}
                          >
                            <Grid3x3 className="h-4 w-4" />
                          </span>
                          <span className="block w-full truncate text-center text-[11px] font-medium leading-tight">
                            Apps
                          </span>
                        </div>
                      ) : (
                        <SidebarNavItem
                          icon={<Grid3x3 className="h-4 w-4" />}
                          active={appsAsideOpen}
                          className={
                            appsAsideOpen
                              ? 'relative before:absolute before:left-0 before:top-2 before:bottom-2 before:w-0.5 before:rounded-r before:bg-primary'
                              : ''
                          }
                        >
                          <span className="truncate">Apps</span>
                        </SidebarNavItem>
                      )}
                    </button>
                  </div>
                </div>
              );
            }

            // MP114 — MSSP nav was previously appended to the
            // Organization section here; now it's its own dedicated
            // section pushed during sections array construction.
            const items = entry.items;
            return (
              <Fragment key={entry.title ?? `s-${sIdx}`}>
              {/* Phones only: the per-app aside is hidden below 768px, so the
                  drawer carries the current app's menu (and its create
                  button) above the organization-wide menu. */}
              {sIdx === 0 && currentApp && !isDesktop && (
                <div className="mb-3 border-b pb-3">
                  <div className="mb-1 px-3 text-[10px] font-semibold uppercase tracking-wider text-muted-foreground/70">
                    {currentApp.shortName}
                  </div>
                  {appPrimaryAction && (
                    <AppPrimaryActionButton
                      action={appPrimaryAction}
                      collapsed={false}
                      onNavigate={() => setMobileNavOpen(false)}
                    />
                  )}
                  {currentApp.sidebar.map((section, aIdx) => (
                    <div key={section.title ?? `a-${aIdx}`} className={aIdx > 0 ? 'mt-2' : undefined}>
                      {section.title && section.title !== currentApp.shortName && (
                        <div className="mb-1 px-3 text-[10px] font-semibold uppercase tracking-wider text-muted-foreground/70">
                          {section.title}
                        </div>
                      )}
                      <div className="flex flex-col gap-0.5">
                        {filterFlagged(section.items).map((it) => (
                          <NavRow
                            key={it.to}
                            item={toNavItem(it, sidebarBadges, flagPreviewKeys)}
                            collapsed={false}
                            depth={0}
                            currentPath={location.pathname}
                            currentHash={location.hash}
                            manualOverride={submenuOverride}
                            onToggleExpand={onToggleSubmenu}
                          />
                        ))}
                      </div>
                    </div>
                  ))}
                </div>
              )}
              <div className={sIdx > 0 ? 'mt-3' : undefined}>
                {entry.title && !collapsed && (
                  <div className="mb-1 px-3 text-[10px] font-semibold uppercase tracking-wider text-muted-foreground/70">
                    {entry.title}
                  </div>
                )}
                <div className="flex flex-col gap-0.5">
                  {items.map((item) => {
                    // MP120 — Items with children get hover-preview
                    // handlers, collapsed or not (operator request, 23
                    // Oct: menus open on hover in the full sidebar too).
                    // Not for the group whose panel is already pinned
                    // open beside the sidebar: the flyout would repeat it.
                    // Wrapping div is no-op for layout.
                    const pinnedOpen = !appsAsideOpen && activeTenantItem?.to === item.to;
                    const previewable = !!item.children?.length && !pinnedOpen;
                    const row = (
                      <NavRow
                        item={item}
                        collapsed={collapsed}
                        depth={0}
                        currentPath={location.pathname}
                        currentHash={location.hash}
                        manualOverride={submenuOverride}
                        onToggleExpand={onToggleSubmenu}
                        // MP67 — Children are surfaced exclusively in
                        // the secondary aside; the main sidebar never
                        // shows an inline dropdown for tenant items
                        // with children. MP85 (user feedback): even
                        // when the item isn't the active route, the
                        // accordion in the main nav was confusing —
                        // clicking the item should just navigate, and
                        // the children only appear in the aside once
                        // the user is on that surface (same shape as
                        // the per-app aside).
                        // Menu review (Oct 2026): on phones the aside is
                        // hidden, so groups expand in place instead.
                        showAsLeaf={isDesktop && !!item.children?.length}
                        // While the Apps panel is open it is the one lit
                        // row; the page's own group lights again when the
                        // panel closes (navigating closes it).
                        suppressActive={appsAsideOpen}
                      />
                    );
                    // Clicking any organization-wide row closes the Apps
                    // panel — including the group you're already on, where
                    // no navigation happens to close it.
                    const closeApps = () => {
                      if (appsAsideOpen) setAppsAsideOpen(false);
                    };
                    if (!previewable) return <div key={item.to} onClick={closeApps}>{row}</div>;
                    return (
                      <div
                        key={item.to}
                        onClick={closeApps}
                        onMouseEnter={() => showPreviewSoon({ kind: 'tenant', item })}
                        onMouseLeave={clearPreviewSoon}
                      >
                        {row}
                      </div>
                    );
                  })}
                </div>
              </div>
              </Fragment>
            );
          })}
        </SidebarNav>
        <SidebarFooter>
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <button
                className={`group flex w-full items-center rounded-md p-2 text-left text-sm transition-colors hover:bg-accent focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring ${collapsed ? 'justify-center' : 'gap-2.5'}`}
                title={collapsed ? org?.name ?? 'Switch organization' : undefined}
              >
                <div className="relative shrink-0">
                  <div className="flex h-8 w-8 items-center justify-center rounded-md bg-gradient-to-br from-blue-500/15 to-purple-500/15 text-xs font-semibold text-foreground ring-1 ring-border">
                    {initials(org?.name ?? session?.activeOrgId ?? '?')}
                  </div>
                  {org?.status && (
                    <span
                      className={`absolute -bottom-0.5 -right-0.5 h-2.5 w-2.5 rounded-full ring-2 ring-background ${
                        ORG_STATUS_DOT[org.status] ?? 'bg-muted'
                      }`}
                      title={org.status}
                    />
                  )}
                </div>
                {!collapsed && (
                  <>
                    <div className="flex min-w-0 flex-1 flex-col">
                      <span className="truncate font-medium">{org?.name ?? 'Loading…'}</span>
                      <div className="flex min-w-0 items-center gap-1.5">
                        {org?.type && <OrgTypeChip type={org.type} />}
                        {org?.status && (
                          <span className="truncate text-xs text-muted-foreground">{org.status}</span>
                        )}
                      </div>
                    </div>
                    <ChevronDown className="h-4 w-4 shrink-0 text-muted-foreground transition-transform group-data-[state=open]:rotate-180" />
                  </>
                )}
              </button>
            </DropdownMenuTrigger>
            <DropdownMenuContent
              align="end"
              className={`w-72 ${FLOATING_PANEL}`}
              style={{ opacity: 1 }}
            >
              <DropdownMenuLabel>Switch organization</DropdownMenuLabel>
              {memberships === null ? (
                <DropdownMenuItem disabled>Loading…</DropdownMenuItem>
              ) : memberships.length === 0 ? (
                <DropdownMenuItem disabled>No memberships</DropdownMenuItem>
              ) : (
                memberships.map((m) => (
                  <DropdownMenuItem
                    key={m.orgId}
                    onClick={() => handleSwitch(m.orgId)}
                    disabled={switching === m.orgId || m.isActive}
                    className="gap-2.5"
                  >
                    <div className="flex h-7 w-7 shrink-0 items-center justify-center rounded-md bg-gradient-to-br from-blue-500/15 to-purple-500/15 text-[10px] font-semibold ring-1 ring-border">
                      {initials(m.orgName)}
                    </div>
                    <div className="flex min-w-0 flex-1 flex-col">
                      <span className="truncate font-medium">{m.orgName}</span>
                      <div className="flex min-w-0 items-center gap-1.5">
                        <OrgTypeChip type={m.orgType} />
                        <span className="truncate text-[11px] text-muted-foreground">
                          {m.orgRoles.join(', ')}
                        </span>
                      </div>
                    </div>
                    {m.isActive && (
                      <span className="shrink-0 rounded bg-emerald-500/10 px-1.5 py-0.5 text-[10px] font-semibold uppercase tracking-wider text-emerald-700 dark:text-emerald-300">
                        active
                      </span>
                    )}
                  </DropdownMenuItem>
                ))
              )}
              {switchError && (
                <>
                  <DropdownMenuSeparator />
                  <DropdownMenuItem disabled className="text-destructive">
                    {switchError}
                  </DropdownMenuItem>
                </>
              )}
            </DropdownMenuContent>
          </DropdownMenu>
          {/* "Powered by" attribution. Hidden when the org's plan unlocks
              the whiteLabel entitlement — a deliberate plan tell so a Pro
              customer immediately sees what they paid for. Also suppressed
              in collapsed mode where there's no horizontal room for it. */}
          {/* MP738 — not on a standalone host: the product stands alone. */}
          {!whiteLabelEntitled && !collapsed && !standalone && (
            <p className="mt-2 px-2 text-[10px] leading-tight text-muted-foreground/60">
              Powered by X-One Cyber Aware
            </p>
          )}
        </SidebarFooter>
      </Sidebar>

      {/* MP120 — Hover-preview overlay for the collapsed primary
          sidebar. Floats just right of the icon rail (left-16)
          over content + any pinned secondary aside. Pointer
          handlers on the panel itself cancel the leave timer so
          the mouse can cross from icon → panel without dismissal.
          Desktop-only — the mobile drawer pattern already gives
          a full-width view of the nav. */}
      {hoverPreview && (
        <FlyoutHoldContext.Provider value={{ hold: cancelPreviewLeave, release: clearPreviewSoon }}>
        <aside
          // White panel, separated from the page by a thin border and
          // a soft shadow (FLOATING_PANEL's light/dark pair). opacity:1
          // stays as the guard against an inherited translucency. Fixed,
          // not absolute: the shell grows with the page, so a long menu
          // would otherwise run past the bottom of the screen.
          className="fixed bottom-0 left-16 top-14 z-40 hidden w-64 flex-col rounded-r-lg border border-l-0 border-slate-200 bg-white shadow-[8px_0_24px_-4px_rgba(15,23,42,0.14)] dark:border-slate-700 dark:bg-slate-900 md:flex"
          style={{ opacity: 1 }}
          onMouseEnter={cancelPreviewLeave}
          onMouseLeave={clearPreviewSoon}
          data-rail-flyout={collapsed ? '' : 'wide'}
        >
          {hoverPreview.kind === 'app' ? (
            <>
              <div className="flex items-center gap-2 border-b px-3 py-2.5">
                <span className="inline-flex h-7 w-7 shrink-0 items-center justify-center rounded-md bg-primary/10 text-primary">
                  {renderIcon(hoverPreview.app.sidebar[0]?.items[0]?.iconKey ?? 'dashboard')}
                </span>
                <div className="min-w-0 flex-1">
                  <div className="truncate text-sm font-semibold">
                    {hoverPreview.app.name}
                  </div>
                </div>
              </div>
              <nav aria-label={`${hoverPreview.app.name} preview`} className="flex-1 overflow-y-auto py-3">
                {hoverPreview.app.sidebar.map((section, sIdx) => (
                  <div
                    key={section.title ?? `s-${sIdx}`}
                    className={sIdx > 0 ? 'mt-3' : undefined}
                  >
                    {section.title && (
                      <div className="mb-1 px-3 text-[10px] font-semibold uppercase tracking-wider text-muted-foreground/70">
                        {section.title}
                      </div>
                    )}
                    <div className="flex flex-col gap-0.5 px-1.5">
                      {filterFlagged(section.items).map((it) => (
                        <NavRow
                          key={it.to}
                          item={toNavItem(it, sidebarBadges, flagPreviewKeys)}
                          collapsed={false}
                          depth={0}
                          currentPath={location.pathname}
                          currentHash={location.hash}
                          manualOverride={submenuOverride}
                          onToggleExpand={onToggleSubmenu}
                        />
                      ))}
                    </div>
                  </div>
                ))}
              </nav>
            </>
          ) : (
            <>
              <div className="flex items-center gap-2 border-b px-3 py-2.5">
                <span className="inline-flex h-7 w-7 shrink-0 items-center justify-center rounded-md bg-primary/10 text-primary">
                  {hoverPreview.item.icon}
                </span>
                <div className="min-w-0 flex-1">
                  <div className="truncate text-sm font-semibold">
                    {hoverPreview.item.label}
                  </div>
                </div>
              </div>
              <nav aria-label={`${hoverPreview.item.label} preview`} className="flex-1 overflow-y-auto py-3">
                <div className="flex flex-col gap-0.5 px-1.5">
                  {!hoverPreview.item.children?.some((c) => c.to.split('#')[0] === hoverPreview.item.to) && (
                  <NavRow
                    item={{ ...hoverPreview.item, end: true, children: undefined }}
                    collapsed={false}
                    depth={0}
                    currentPath={location.pathname}
                    currentHash={location.hash}
                    manualOverride={submenuOverride}
                    onToggleExpand={onToggleSubmenu}
                  />
                  )}
                  {hoverPreview.item.children?.map((child, i, all) => (
                    <Fragment key={`${child.to}|${child.label}`}>
                    <GroupHeading items={all} index={i} />
                    <NavRow
                      item={child}
                      collapsed={false}
                      depth={0}
                      hoverFlyout
                      currentPath={location.pathname}
                      currentHash={location.hash}
                      manualOverride={submenuOverride}
                      onToggleExpand={onToggleSubmenu}
                    />
                    </Fragment>
                  ))}
                </div>
              </nav>
            </>
          )}
        </aside>
        </FlyoutHoldContext.Provider>
      )}

      {/* MP67 — Tenant secondary sidebar. Mirrors the per-app
          aside shape (below) but for tenant items that have
          children — e.g. KPIs (children: New KPI / New dashboard).
          Active when the URL matches a tenant item with children
          and we're not inside an app (currentApp takes priority,
          enforced inside the activeTenantItem useMemo). */}
      {activeTenantItem && tenantAsideChildren.length > 0 && !appsAsideOpen && (
        // Held to the screen's height so a long menu scrolls inside the
        // panel instead of stretching the whole page.
        <aside
          className={`hidden shrink-0 flex-col border-r bg-card md:sticky md:top-0 md:flex md:h-screen md:self-start ${secondaryCollapsed ? 'w-14' : 'w-52'}`}
        >
          <div className={`flex items-center gap-2 border-b px-3 py-3 ${secondaryCollapsed ? 'justify-center px-2' : ''}`}>
            <span className="inline-flex h-7 w-7 shrink-0 items-center justify-center rounded-md bg-primary/10 text-primary">
              {renderIcon(activeTenantItem.iconKey)}
            </span>
            {!secondaryCollapsed && (
              <div className="min-w-0 flex-1">
                <div className="truncate text-xs font-semibold uppercase tracking-wider text-muted-foreground/80">
                  {activeTenantItem.label}
                </div>
              </div>
            )}
            {/* MP68 — Collapse toggle. Hidden in fully-collapsed mode
                in favor of a sole-icon expand button below to avoid
                two icons fighting for the tiny strip. */}
            {!secondaryCollapsed && (
              <button
                type="button"
                onClick={() => setSecondaryCollapsed(true)}
                className="inline-flex h-7 w-7 shrink-0 items-center justify-center rounded-md text-muted-foreground hover:bg-accent hover:text-foreground"
                aria-label="Collapse secondary navigation"
                title="Collapse"
              >
                <PanelLeftClose className="h-4 w-4" />
              </button>
            )}
          </div>
          {secondaryCollapsed && (
            <button
              type="button"
              onClick={() => setSecondaryCollapsed(false)}
              className="mx-auto mt-1 inline-flex h-7 w-7 items-center justify-center rounded-md text-muted-foreground hover:bg-accent hover:text-foreground"
              aria-label="Expand secondary navigation"
              title="Expand"
            >
              <PanelLeftOpen className="h-4 w-4" />
            </button>
          )}
          <nav aria-label={activeTenantItem ? activeTenantItem.label : "Section"} className="flex-1 overflow-y-auto py-3">
            <div className={`flex flex-col gap-0.5 ${secondaryCollapsed ? 'px-1' : 'px-1.5'}`}>
              {/* MP121 — Skip the parent leaf when the parent's
                  route is also one of its children (the
                  consolidated Reporting / Identity / Integrations
                  case where the parent points at its first child
                  as a landing target). Otherwise render it so the
                  user can always click back to the parent's own
                  page from within the section (e.g. /policies). */}
              {!tenantAsideChildren.some(
                (c) => c.to.split('#')[0] === activeTenantItem.to.split('#')[0],
              ) && (
                <NavRow
                  item={toNavItem({ ...activeTenantItem, end: true, children: undefined }, sidebarBadges, flagPreviewKeys)}
                  collapsed={secondaryCollapsed}
                  depth={0}
                  currentPath={location.pathname}
                  currentHash={location.hash}
                  manualOverride={submenuOverride}
                  onToggleExpand={onToggleSubmenu}
                />
              )}
              {tenantAsideChildren.map((child, i, all) => (
                <Fragment key={`${child.to}|${child.label}`}>
                {!secondaryCollapsed && <GroupHeading items={all} index={i} />}
                <NavRow
                  item={toNavItem(child, sidebarBadges, flagPreviewKeys)}
                  collapsed={secondaryCollapsed}
                  depth={0}
                  hoverFlyout
                  currentPath={location.pathname}
                  currentHash={location.hash}
                  manualOverride={submenuOverride}
                  onToggleExpand={onToggleSubmenu}
                />
                </Fragment>
              ))}
            </div>
          </nav>
        </aside>
      )}

      {/* MP122 — Dedicated Apps aside. Pure toggle: renders only
          when the user has clicked the Apps row in the main
          sidebar (appsAsideOpen). Auto-closes when the user
          enters or switches into an app (handled by the
          prevAppKeyRef effect), so on an app URL the per-app
          aside takes over without doubling up on horizontal nav
          chrome. Active app is highlighted while the aside is
          open so app-switching is one click. */}
      {appsAsideOpen && (entitledApps.length > 0 || externalApps.length > 0) && (
        <aside
          className={`hidden shrink-0 flex-col border-r bg-card md:flex ${secondaryCollapsed ? 'w-14' : 'w-52'}`}
        >
          <div className={`flex items-center gap-2 border-b px-3 py-3 ${secondaryCollapsed ? 'justify-center px-2' : ''}`}>
            <span className="inline-flex h-7 w-7 shrink-0 items-center justify-center rounded-md bg-primary/10 text-primary">
              <Grid3x3 className="h-4 w-4" />
            </span>
            {!secondaryCollapsed && (
              <div className="min-w-0 flex-1">
                <div className="truncate text-xs font-semibold uppercase tracking-wider text-muted-foreground/80">
                  Apps
                </div>
                <div className="truncate text-[10px] text-muted-foreground/70">
                  {entitledApps.length + externalApps.length} entitled
                </div>
              </div>
            )}
          </div>
          <nav aria-label="Apps" className="flex-1 overflow-y-auto py-3">
            <div className={`flex flex-col gap-0.5 ${secondaryCollapsed ? 'px-1' : 'px-1.5'}`}>
              {entitledApps.map((app) => (
                <NavLink
                  key={app.key}
                  to={app.entryRoute!}
                  className="block"
                  title={secondaryCollapsed ? app.shortName : undefined}
                  // MP122 — Close the aside synchronously on click
                  // so the per-app aside is the only thing visible
                  // after navigation completes. The pathname
                  // useEffect also closes it, but that runs after
                  // render and would briefly show both asides.
                  onClick={() => setAppsAsideOpen(false)}
                >
                  {({ isActive }) => (
                    <SidebarNavItem
                      icon={renderIcon(app.sidebar[0]?.items[0]?.iconKey ?? 'dashboard')}
                      active={isActive}
                      className={`${
                        isActive
                          ? 'relative before:absolute before:left-0 before:top-2 before:bottom-2 before:w-0.5 before:rounded-r before:bg-primary'
                          : ''
                      } ${secondaryCollapsed ? 'justify-center px-2' : ''}`}
                    >
                      {secondaryCollapsed ? null : app.shortName}
                    </SidebarNavItem>
                  )}
                </NavLink>
              ))}
            </div>
            {/* External products — entitled apps that live on their own
                host (none since ADR 0030). Plain anchors in a new tab, never
                NavLinks: they are not in-shell routes and must not be
                treated as the "current app". Same entitlement gate as
                the launcher's external tile. */}
            {externalApps.length > 0 && (
              <div className={`mt-3 flex flex-col gap-0.5 border-t pt-3 ${secondaryCollapsed ? 'px-1' : 'px-1.5'}`}>
                {!secondaryCollapsed && (
                  <div className="px-2 pb-1 text-[10px] font-semibold uppercase tracking-wider text-muted-foreground/70">
                    External products
                  </div>
                )}
                {externalApps.map((app) => (
                  <a
                    key={app.key}
                    href={app.externalUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="block"
                    title={secondaryCollapsed ? `${app.shortName} — opens in a new tab` : `Opens ${app.name} in a new tab`}
                    onClick={() => setAppsAsideOpen(false)}
                  >
                    <SidebarNavItem
                      icon={<ExternalLink className="h-4 w-4" />}
                      active={false}
                      className={secondaryCollapsed ? 'justify-center px-2' : ''}
                    >
                      {secondaryCollapsed ? null : (
                        <span className="inline-flex items-center gap-1">
                          {app.shortName}
                          <span aria-hidden="true" className="text-muted-foreground/70">↗</span>
                        </span>
                      )}
                    </SidebarNavItem>
                  </a>
                ))}
              </div>
            )}
          </nav>
        </aside>
      )}

      {/* MP55 — Per-app secondary sidebar. Renders to the right of
          the main sidebar when the user is INSIDE an app
          (currentApp !== null — actually on an app URL, not just
          last-visited). Hidden on mobile (the main sidebar already
          uses a drawer; adding a second column would be cramped).
          Hidden on the launcher / tenant-wide pages — those don't
          have a per-app context to surface.

          MP122 — Suppressed while the Apps aside is open so the
          two asides never coexist. The Apps aside takes the
          secondary slot exclusively while toggled on; closing it
          (or navigating) restores the per-app aside. */}
      {currentApp && currentApp.sidebar.length > 0 && !appsAsideOpen && (
        <aside
          className={`hidden shrink-0 flex-col border-r bg-card md:flex ${secondaryCollapsed ? 'w-14' : 'w-52'}`}
        >
          <div className={`flex items-center gap-2 border-b px-3 py-3 ${secondaryCollapsed ? 'justify-center px-2' : ''}`}>
            <span className="inline-flex h-7 w-7 shrink-0 items-center justify-center rounded-md bg-primary/10 text-primary">
              {renderIcon(currentApp.sidebar[0]?.items[0]?.iconKey ?? 'dashboard')}
            </span>
            {!secondaryCollapsed && (
              <div className="min-w-0 flex-1">
                <div className="truncate text-xs font-semibold uppercase tracking-wider text-muted-foreground/80">
                  {currentApp.shortName}
                </div>
                <div className="truncate text-[10px] text-muted-foreground/70">
                  {currentApp.name}
                </div>
              </div>
            )}
            {/* MP68 — Collapse toggle. */}
            {!secondaryCollapsed && (
              <button
                type="button"
                onClick={() => setSecondaryCollapsed(true)}
                className="inline-flex h-7 w-7 shrink-0 items-center justify-center rounded-md text-muted-foreground hover:bg-accent hover:text-foreground"
                aria-label="Collapse secondary navigation"
                title="Collapse"
              >
                <PanelLeftClose className="h-4 w-4" />
              </button>
            )}
          </div>
          {secondaryCollapsed && (
            <button
              type="button"
              onClick={() => setSecondaryCollapsed(false)}
              className="mx-auto mt-1 inline-flex h-7 w-7 items-center justify-center rounded-md text-muted-foreground hover:bg-accent hover:text-foreground"
              aria-label="Expand secondary navigation"
              title="Expand"
            >
              <PanelLeftOpen className="h-4 w-4" />
            </button>
          )}
          <nav aria-label={currentApp ? currentApp.name : "App"} className="flex-1 overflow-y-auto py-3">
            {currentApp.sidebar.map((section, sIdx) => (
              <div key={section.title ?? `s-${sIdx}`} className={sIdx > 0 ? 'mt-3' : undefined}>
                {sIdx === 0 && appPrimaryAction && (
                  <AppPrimaryActionButton action={appPrimaryAction} collapsed={secondaryCollapsed} />
                )}
                {section.title && !secondaryCollapsed && (
                  <div className="mb-1 px-3 text-[10px] font-semibold uppercase tracking-wider text-muted-foreground/70">
                    {section.title}
                  </div>
                )}
                <div className={`flex flex-col gap-0.5 ${secondaryCollapsed ? 'px-1' : 'px-1.5'}`}>
                  {/* MP58 — Reuse NavRow so sub-menu support flows
                      into the secondary sidebar identically to the
                      main one. MP68 — Collapse state passes through.
                      MP316c — Same featureFlag filter as the primary
                      sidebar so per-app items (e.g. XES → Rules) drop
                      out when the flag is disabled. */}
                  {filterFlagged(section.items).map((it) => (
                    <NavRow
                      key={it.to}
                      item={toNavItem(it, sidebarBadges, flagPreviewKeys)}
                      collapsed={secondaryCollapsed}
                      depth={0}
                      currentPath={location.pathname}
                      currentHash={location.hash}
                      manualOverride={submenuOverride}
                      onToggleExpand={onToggleSubmenu}
                    />
                  ))}
                </div>
              </div>
            ))}
          </nav>
        </aside>
      )}

      <AppMain>
        <AppHeader>
          <AppHeaderLeft>
            {/* Hamburger — only on mobile. Above md the sidebar is always
                visible so the toggle is unnecessary. */}
            <button
              type="button"
              ref={mobileNavButtonRef}
              onClick={() => setMobileNavOpen(true)}
              aria-expanded={mobileNavOpen}
              className="-ml-1 inline-flex h-9 w-9 items-center justify-center rounded-md text-muted-foreground hover:bg-accent hover:text-foreground md:hidden"
              aria-label="Open navigation"
            >
              <Menu className="h-5 w-5" />
            </button>
            <h1 className="text-sm font-semibold tracking-tight">{title}</h1>
            {titleSlot && <div className="flex min-w-0 items-center">{titleSlot}</div>}
          </AppHeaderLeft>
          <AppHeaderRight>
            {/* Search: the visible way into the command palette, so the
                feature never depends on knowing a shortcut. Shows the
                platform's own modifier key. */}
            <button
              type="button"
              onClick={() => setPaletteOpen(true)}
              aria-keyshortcuts="Control+K Meta+K /"
              aria-haspopup="dialog"
              className="inline-flex h-9 items-center gap-2 rounded-md border border-border px-2 text-sm text-muted-foreground hover:bg-accent hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring sm:min-w-[11rem]"
              title={standalone ? 'Search pages' : 'Search pages across every app'}
            >
              <Search className="h-4 w-4" aria-hidden="true" />
              <span className="hidden sm:inline">Search pages…</span>
              <span className="sr-only sm:hidden">Search pages</span>
              <kbd className="ml-auto hidden rounded border border-border bg-muted px-1.5 font-mono text-[11px] text-muted-foreground sm:inline">
                {IS_MAC ? '⌘K' : 'Ctrl K'}
              </kbd>
            </button>
            {/* MP251 — "Ask Athena" launcher. Deep-links to the
                copilot page. Restricted to admins/owners because
                the copilot itself is admin-gated.
                MP316c — Additionally gated behind the `athena.copilot`
                feature flag so the platform team can dark-launch v2
                tool-use rollouts (MP252 arc, per project_athena_copilot
                memory) to a subset of tenants without hiding the
                surface globally. Missing flag = visible; only explicit
                disabled hides. */}
            {athenaAllowed && !onAthenaPage && (
              <button
                type="button"
                onClick={() => athena?.setPanelOpen(!athena.panelOpen)}
                className={`inline-flex h-9 items-center gap-1.5 rounded-md px-2 text-sm hover:bg-accent hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring ${
                  athenaPanelOpen ? 'bg-accent text-foreground' : 'text-muted-foreground'
                }`}
                aria-label="Ask Athena"
                aria-expanded={athenaPanelOpen}
                title="Ask Athena about this page (Ctrl or ⌘ + /)"
              >
                <Sparkles className="h-4 w-4 text-violet-500" />
                <span className="hidden sm:inline">Ask Athena</span>
              </button>
            )}
            {/* App switcher (sprint MP7). Waffle-grid icon opens a
                dropdown listing every X-One app the org is entitled to;
                clicking jumps straight into that app's entry route.
                When only one app is entitled the dropdown is a no-op,
                so we fall back to the simple launcher link. */}
            {/* MP738 — no switcher and no launcher link on a standalone
                host: there is one product and no Security overview. */}
            {standalone ? null : entitledApps.length >= 2 ? (
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <button
                    type="button"
                    className="inline-flex h-9 items-center gap-1.5 rounded-md px-2 text-sm text-muted-foreground hover:bg-accent hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
                    aria-label="Switch app"
                  >
                    <Grid3x3 className="h-4 w-4" />
                    {currentApp ? (
                      <span className="hidden sm:inline">{currentApp.shortName}</span>
                    ) : (
                      <span className="hidden sm:inline">Apps</span>
                    )}
                    <ChevronDown className="h-3.5 w-3.5" />
                  </button>
                </DropdownMenuTrigger>
                <DropdownMenuContent
                  align="end"
                  sideOffset={6}
                  collisionPadding={16}
                  // Grid of tiles (operator requests, 23 Oct): three across from
                  // 640px, one column on phones; each app has its own tinted
                  // badge (APP_TINT) so it is found by colour as well as icon.
                  // Arrow keys move in all four directions (gridMenuKeyDown).
                  className={`w-[min(92vw,44rem)] p-2 ${FLOATING_PANEL}`}
                  style={{ opacity: 1 }}
                >
                  <DropdownMenuLabel className="flex items-baseline justify-between gap-3 px-2 pb-2 pt-1">
                    <span>
                      <span className="block text-sm font-semibold text-foreground">Your apps</span>
                      <span className="block text-[11px] font-normal text-muted-foreground">
                        Jump straight into any X-One app
                      </span>
                    </span>
                    <span className="shrink-0 text-[11px] font-normal tabular-nums text-muted-foreground">
                      {entitledApps.length + externalApps.length} apps
                    </span>
                  </DropdownMenuLabel>
                  <div data-menu-grid className="grid grid-cols-1 gap-1.5 sm:grid-cols-3">
                    {entitledApps.map((app) => {
                      const current = currentApp?.key === app.key;
                      return (
                        <DropdownMenuItem
                          key={app.key}
                          data-menu-grid-item
                          onKeyDown={gridMenuKeyDown}
                          onClick={() => !current && app.entryRoute && navigate(app.entryRoute)}
                          aria-current={current ? 'page' : undefined}
                          className={`group h-full flex-col items-start gap-2 rounded-xl border p-3 transition-colors focus:bg-slate-50 dark:focus:bg-slate-800/60 ${
                            current
                              ? 'border-indigo-300 bg-indigo-50/70 dark:border-indigo-700 dark:bg-indigo-950/40'
                              : 'border-transparent hover:border-slate-200 focus:border-slate-200 dark:hover:border-slate-700 dark:focus:border-slate-700'
                          }`}
                        >
                          <span className="flex w-full items-center gap-2.5">
                            <span className={`inline-flex h-9 w-9 shrink-0 items-center justify-center rounded-lg ring-1 ${APP_TINT[app.key] ?? APP_TINT_DEFAULT}`}>
                              {renderIcon(app.iconKey, 'h-[18px] w-[18px]')}
                            </span>
                            <span className="min-w-0 flex-1">
                              <span className="block truncate text-sm font-semibold text-foreground">{app.shortName}</span>
                              {current && (
                                <span className="block text-[10px] font-semibold uppercase tracking-wider text-indigo-600 dark:text-indigo-300">
                                  You are here
                                </span>
                              )}
                            </span>
                            {!current && (
                              <ArrowRight
                                className="h-3.5 w-3.5 shrink-0 text-muted-foreground opacity-0 transition-opacity group-hover:opacity-100 group-focus:opacity-100"
                                aria-hidden="true"
                              />
                            )}
                          </span>
                          <span className="line-clamp-2 text-[11px] font-normal leading-snug text-muted-foreground">
                            {app.tagline}
                          </span>
                        </DropdownMenuItem>
                      );
                    })}
                    {externalApps.map((app) => (
                      <DropdownMenuItem key={app.key} asChild data-menu-grid-item onKeyDown={gridMenuKeyDown}>
                        <a
                          href={app.externalUrl}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="group h-full flex-col items-start gap-2 rounded-xl border border-transparent p-3 transition-colors hover:border-slate-200 focus:border-slate-200 focus:bg-slate-50 dark:hover:border-slate-700 dark:focus:border-slate-700 dark:focus:bg-slate-800/60"
                        >
                          <span className="flex w-full items-center gap-2.5">
                            <span className={`inline-flex h-9 w-9 shrink-0 items-center justify-center rounded-lg ring-1 ${APP_TINT[app.key] ?? APP_TINT_DEFAULT}`}>
                              {renderIcon(app.iconKey, 'h-[18px] w-[18px]')}
                            </span>
                            <span className="min-w-0 flex-1">
                              <span className="block truncate text-sm font-semibold text-foreground">{app.shortName}</span>
                              <span className="block text-[10px] font-medium text-muted-foreground">Opens in a new tab</span>
                            </span>
                            <ExternalLink className="h-3.5 w-3.5 shrink-0 text-muted-foreground" aria-hidden="true" />
                          </span>
                          <span className="line-clamp-2 text-[11px] font-normal leading-snug text-muted-foreground">
                            {app.tagline}
                          </span>
                        </a>
                      </DropdownMenuItem>
                    ))}
                  </div>
                  <DropdownMenuSeparator className="my-2" />
                  <DropdownMenuItem onClick={() => navigate('/launcher')} className="gap-2 px-2.5">
                    <Grid3x3 className="h-4 w-4" aria-hidden="true" />
                    <span>Security overview</span>
                    <span className="ml-auto text-[11px] font-normal text-muted-foreground">every app at a glance</span>
                    <ArrowRight className="h-3.5 w-3.5 text-muted-foreground" aria-hidden="true" />
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            ) : (
              <BackToAppsButton />
            )}
            <Separator orientation="vertical" className="h-6" />
            {/* Help (?): What's new, documentation, getting started,
                shortcuts, Athena, support and status in one menu; a dot
                marks unseen product updates. Replaces the sidebar's
                Help group. */}
            {/* Notifications for things that need you or just happened in
                the org (approvals, a connector that stopped syncing...). */}
            <NotificationsBell />
            <HelpMenu
              onOpenShortcuts={() => setShortcutsOpen(true)}
              onAskAthena={athenaAllowed && !onAthenaPage ? () => athena?.setPanelOpen(true) : undefined}
            />
            {rightSlot}
            {rightSlot && <Separator orientation="vertical" className="h-6" />}
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <button aria-label="Account menu" className="flex items-center gap-2 rounded-full ring-2 ring-transparent transition-shadow hover:ring-border focus-visible:outline-none focus-visible:ring-ring">
                  <Avatar>
                    <AvatarFallback className="bg-gradient-to-br from-blue-500/20 to-purple-500/20 font-semibold">
                      {initials(session?.displayName ?? session?.email)}
                    </AvatarFallback>
                  </Avatar>
                </button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-56">
                <DropdownMenuLabel>
                  <div className="flex flex-col">
                    <span className="font-medium">{session?.displayName ?? 'You'}</span>
                    <span className="truncate text-xs font-normal text-muted-foreground">{session?.email}</span>
                  </div>
                </DropdownMenuLabel>
                <DropdownMenuSeparator />
                <DropdownMenuItem onClick={() => navigate('/shell/profile')}>
                  <UserCircle className="h-4 w-4" /> Profile
                </DropdownMenuItem>
                {/* The employee view is the awareness-training portal, a
                    suite surface, so it is not offered on a standalone host. */}
                {!standalone && (
                  <>
                    <DropdownMenuSeparator />
                    <DropdownMenuItem onClick={() => navigate('/learn')}>
                      <GraduationCap className="h-4 w-4" /> View as employee
                    </DropdownMenuItem>
                  </>
                )}
                <DropdownMenuSeparator />
                <ThemeToggle />
                <DropdownMenuSeparator />
                <DropdownMenuItem onClick={handleSignOut}>
                  <LogOut className="h-4 w-4" /> Sign out
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </AppHeaderRight>
        </AppHeader>

        <AppContent ref={appContentRef} id="main-content" tabIndex={-1} className="focus:outline-none">{children}</AppContent>
      </AppMain>
      {athenaPanelOpen && <AthenaPanel pathname={location.pathname} title={title} />}
    </AppShell>
  );
}

// Status dot tone for the org-switcher avatar — surfaces lifecycle state
// without taking up a row of text in the footer.
const PALETTE_RECENTS_KEY = 'xone.palette.recents.v1';

/** Pop-out menus (org switcher, app switcher): a white panel set off
 *  from the page by a thin border, a soft shadow and a faint ring — the
 *  same treatment Wiz uses — with a dark-mode pair that follows the
 *  theme toggle. Row text matches the sidebar menus exactly: the theme's
 *  muted foreground in medium weight, darkening on hover (operator
 *  feedback 22 Oct). Replaces the old hardcoded grey, which read as heavy
 *  and ignored dark mode. */
/** One tint per app for its icon badge in the apps grid (operator
 *  request, 23 Oct: "a little plain"). Cool family only — no red, orange
 *  or green, which mean severity elsewhere — with a dark-mode pair. Full
 *  class strings so Tailwind keeps them. The Focus menu mirrors these for
 *  the area each app leads. */
const APP_TINT: Record<string, string> = {
  aware: 'bg-violet-50 text-violet-600 ring-violet-100 dark:bg-violet-950/50 dark:text-violet-300 dark:ring-violet-900',
  xes: 'bg-sky-50 text-sky-600 ring-sky-100 dark:bg-sky-950/50 dark:text-sky-300 dark:ring-sky-900',
  posture: 'bg-blue-50 text-blue-600 ring-blue-100 dark:bg-blue-950/50 dark:text-blue-300 dark:ring-blue-900',
  surface: 'bg-indigo-50 text-indigo-600 ring-indigo-100 dark:bg-indigo-950/50 dark:text-indigo-300 dark:ring-indigo-900',
  // MP900 — teal: the one cool hue no other app uses. Not a status
  // colour (healthy is emerald/green), and distinct from Orchestrate's cyan.
  exposure: 'bg-teal-50 text-teal-700 ring-teal-100 dark:bg-teal-950/50 dark:text-teal-300 dark:ring-teal-900',
  agents: 'bg-purple-50 text-purple-600 ring-purple-100 dark:bg-purple-950/50 dark:text-purple-300 dark:ring-purple-900',
  orchestrate: 'bg-cyan-50 text-cyan-700 ring-cyan-100 dark:bg-cyan-950/50 dark:text-cyan-300 dark:ring-cyan-900',
  insider: 'bg-fuchsia-50 text-fuchsia-600 ring-fuchsia-100 dark:bg-fuchsia-950/50 dark:text-fuchsia-300 dark:ring-fuchsia-900',
  resilience: 'bg-slate-100 text-slate-700 ring-slate-200 dark:bg-slate-800 dark:text-slate-200 dark:ring-slate-700',
  'ai-gateway': 'bg-pink-50 text-pink-600 ring-pink-100 dark:bg-pink-950/50 dark:text-pink-300 dark:ring-pink-900',
  // stone: a warm grey, distinct from Resilience's slate and Umbra's zinc.
  // Every remaining hue (rose, lime, yellow) reads as a status colour.
  'ai-security': 'bg-stone-100 text-stone-700 ring-stone-200 dark:bg-stone-800 dark:text-stone-200 dark:ring-stone-700',
  // MP480 — zinc: a neutral grey distinct from Resilience's slate.
  umbra: 'bg-zinc-100 text-zinc-700 ring-zinc-200 dark:bg-zinc-800 dark:text-zinc-200 dark:ring-zinc-700',
};
const APP_TINT_DEFAULT = APP_TINT.surface;

const FLOATING_PANEL =
  'border-slate-200 bg-white font-medium text-muted-foreground shadow-xl ring-1 ring-slate-900/5 dark:border-slate-700 dark:bg-slate-900';

/** Read once: shows ⌘K on Apple platforms and Ctrl K elsewhere. */
const IS_MAC = typeof navigator !== 'undefined' && /mac|iphone|ipad/i.test(navigator.platform || navigator.userAgent);

const ORG_STATUS_DOT: Record<string, string> = {
  trial: 'bg-blue-500',
  active: 'bg-emerald-500',
  suspended: 'bg-amber-500',
  archived: 'bg-muted-foreground/40',
};

// Tone-mapped chip for org tenancy type — quickly distinguishes a standalone
// SMB from an MSSP child or a community-shared org in the switcher.
const ORG_TYPE_TONE: Record<string, string> = {
  standalone: 'bg-blue-500/10 text-blue-700 dark:text-blue-300',
  mssp: 'bg-cyan-500/10 text-cyan-700 dark:text-cyan-300',
  community: 'bg-violet-500/10 text-violet-700 dark:text-violet-300',
};

function OrgTypeChip({ type }: { type: string }) {
  const tone = ORG_TYPE_TONE[type] ?? 'bg-muted text-muted-foreground';
  return (
    <span className={`shrink-0 rounded px-1.5 py-0.5 text-[10px] font-semibold uppercase tracking-wider ${tone}`}>
      {type}
    </span>
  );
}
